/* In-house personalizer — storefront port of reports/3d/viewer.html.
 *
 * Reads its whole spec from the product's custom.design_zone metafield (serialised into
 * data-zone), so a zone change is a metafield edit, not a theme deploy.
 *
 * Carried over from the prototype, each learned the expensive way:
 *  - Guides and artwork live on SEPARATE canvases. The art canvas is what gets pushed to
 *    the 3D model as a texture, so anything drawn for the editor would be printed.
 *  - The scrim spans the whole product photo. Confined to the artboard, its own edge
 *    reads as a grey rectangle around the real shape.
 *  - Placement is never blocked. Refusing a drag makes the editor feel broken and traps
 *    the customer in a bad layout. Violations are reported and gate Save.
 *  - Bounds and overlap test the ROTATED bounding box, and clamp the centre, not the box.
 *  - model-viewer's .src is null (not '') before first assignment, so .includes() on it
 *    throws and kills the module.
 *
 * Deliberately NOT carried over: the prototype's ?v=Date.now() cache-buster. Shopify CDN
 * URLs already carry ?v=<version> and change when the file changes; adding a second stamp
 * would defeat caching on every page view.
 */
(() => {
  const MV_SRC = 'https://ajax.googleapis.com/ajax/libs/model-viewer/4.0.0/model-viewer.min.js';
  // How far a digitised alphabet may be scaled down before its stitch density needs redoing
  // by hand. Must match SAFE_SCALE[0] in scripts/pes_compose.py — that is the script that
  // actually builds the .PES, and a customer must not be able to approve a design it would
  // have to warn about.
  const SAFE_SCALE_MIN = 0.8;
  const SAFE_SCALE_MAX = 1.25;
  // Margin kept clear inside a design area — you do not stitch to the edge of a hem.
  // Must match FIT in scripts/check_zone_scale.py, which derives zone.max_chars from it.
  const FIT = 0.92;

  // FALLBACK ONLY. The real palettes are the shop's own stock — 39 embroidery threads and
  // 22 vinyl sheets — extracted from its reference photos into personalizer-colors.json
  // by scripts/icons/build_color_catalog.py. These seven were invented, and offering a
  // customer a colour the shop does not stock is a preview that lies about the finished
  // product. Kept only so a failed fetch still leaves a usable panel.
  const THREADS = [
    { hex: '#F2E5C4', name: 'Cream' }, { hex: '#FFFFFF', name: 'White' },
    { hex: '#572127', name: 'Burgundy' }, { hex: '#7A9A8B', name: 'Sage' },
    { hex: '#1F2A44', name: 'Navy' }, { hex: '#B8733F', name: 'Tan' },
    { hex: '#111111', name: 'Black' },
  ];
  // The two system faces Zepto offered. The other three it offered are the shop's own
  // uploads and arrive from personalizer-fonts.json as `webfonts`.
  //
  // These replaced Snell/Didot/Futura, which were placeholders and a quiet lie: they are
  // macOS system faces, so a Windows customer was previewing a fallback, and no vinyl in
  // this shop was ever cut in any of the three.
  const FONTS = [
    { label: 'Arial', css: 'Arial, Helvetica, sans-serif' },
    { label: 'Times New Roman', css: '"Times New Roman", Times, serif' },
  ];

  /** model-viewer throws an uncaught TypeError when it cannot get a WebGL context, which
   *  kills the module. Check first and stay in 2D instead — 2D is the source of truth
   *  for production anyway, so losing 3D costs the customer nothing. */
  let webglOK = null;
  function hasWebGL() {
    if (webglOK !== null) return webglOK;
    try {
      const c = document.createElement('canvas');
      webglOK = !!(c.getContext('webgl2') || c.getContext('webgl'));
    } catch {
      webglOK = false;
    }
    return webglOK;
  }

  let mvLoading = null;
  const loadModelViewer = () => (mvLoading ??= new Promise((res, rej) => {
    const s = document.createElement('script');
    s.type = 'module';
    s.src = MV_SRC;
    s.onload = res;
    s.onerror = () => rej(new Error('model-viewer failed to load'));
    document.head.append(s);
  }));

  class PersonalizerPanel extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      try {
        this.zone = JSON.parse(this.dataset.zone);
      } catch (err) {
        // A broken metafield must not take the Add to Cart button down with it.
        console.error('[personalizer] bad design_zone metafield', err);
        this.hidden = true;
        return;
      }
      // ⚠️ WHILE ZEPTO IS STILL INSTALLED — delete with it (phase 7).
      //
      // Zepto keys its setup to the PRODUCT ID, so consolidating 33 books into 9 left the
      // new IDs unconfigured and personalization vanished from every notebook and planner.
      // This panel covers them; Zepto still covers the sweater, pouch, hat, napkin and
      // towel. Showing both on one page would be worse than either.
      //
      // Asked at RUNTIME rather than scoped in Liquid, because a hardcoded list cannot
      // know that Zepto's embed is disabled on the draft theme — which left those products
      // with NO personalizer at all there. Zepto declares this variable inline, and only
      // on products it actually handles, so the answer is always right for the page we are
      // on and needs no per-theme configuration.
      const zp = window.product_personalizer;
      if (zp && (+zp.pplr_req > 0 || +zp.cstmfy_req > 0)) {
        this.hidden = true;
        return;
      }

      this.admin = new URLSearchParams(location.search).has('admin');
      this.form = document.getElementById(this.dataset.formId);
      this.wireCart();
      this.icons = [];
      this.sides = this.zone.sides?.length ? this.zone.sides : ['front'];
      this.side = this.sides[0];
      this.design = Object.fromEntries(this.sides.map((s) => [s, []]));
      this.sel = null;
      this.mode = '2d';
      this.applied = false;

      // The stage is later moved OUT of this element into the product media column, so
      // every lookup has to consider both roots or it silently returns null once moved.
      this.stageEl = this.querySelector('.pz__stage');
      this.q = (n) => this.querySelector(`[data-pz="${n}"]`)
        || this.stageEl?.querySelector(`[data-pz="${n}"]`) || null;
      this.qa = (sel) => [...this.querySelectorAll(sel),
        ...(this.stageEl && !this.contains(this.stageEl) ? this.stageEl.querySelectorAll(sel) : [])];
      this.artboard = this.q('artboard');
      this.ctx = this.artboard.getContext('2d');
      this.art = document.createElement('canvas');           // artwork only — no guides
      this.actx = this.art.getContext('2d');
      this.flat = this.q('flat');
      this.fctx = this.flat.getContext('2d');

      this.resizeArtboard();
      this.wire();
      this.showSpec();
      this.render();
    }

    disconnectedCallback() {
      if (typeof this.unsubscribeVariant === 'function') this.unsubscribeVariant();
    }

    get els() { return this.design[this.side]; }
    set els(v) { this.design[this.side] = v; }

    // ---------- geometry ----------
    resizeArtboard() {
      const z = this.zone.designable;
      const MAX = 700;
      // Cap the LONGER side, not the width. A spine is 13x186mm: fixing the width at 700
      // would allocate a 700x10015 canvas — 28 million pixels for a strip of text — and
      // the artboard, the artwork canvas and the 3D texture all share this size.
      if (z.width >= z.height) {
        this.CW = MAX;
        this.CH = Math.round(MAX * z.height / z.width);
      } else {
        this.CH = MAX;
        this.CW = Math.round(MAX * z.width / z.height);
      }
      this.artboard.width = this.CW;
      this.artboard.height = this.CH;
      this.art.width = this.CW;
      this.art.height = this.CH;
    }

    fontFor(e) {
      return e.type === 'icon' ? `${e.size}px system-ui` : `${e.size}px ${e.font.css}`;
    }

    bbox(e) {
      if (e.type === 'upload' || e.type === 'icon') {
        // The DIGITISED proportions first. naturalWidth is 0 until the PNG arrives, and the
        // square fallback that covers that gap is not a neutral guess — loadIconImage
        // clamps immediately, so a tall icon was measured as square and mis-sized on the
        // way in, with nothing to correct it once the image landed. iconMm is known before
        // the request is made, and it is what the machine will sew.
        if (e.iconMm?.w && e.iconMm?.h) return { w: e.size * (e.iconMm.w / e.iconMm.h), h: e.size };
        if (e.img?.naturalWidth) {
          return { w: e.size * (e.img.naturalWidth / e.img.naturalHeight || 1), h: e.size };
        }
        return { w: e.size, h: e.size };
      }
      if (e.font?.glyphs) {
        const m = this.stitchMetrics(e);
        return { w: m.wMm * m.scale, h: e.size };
      }
      this.ctx.font = this.fontFor(e);
      return { w: this.ctx.measureText(e.value).width, h: e.size * 0.9 };
    }

    /** The angle a design starts at in this area. A spine is a tall narrow strip, so
     *  text has to run ALONG it — starting at 0° would put every name across the
     *  width and instantly out of bounds. Customer can still rotate freely. */
    startRot() { return +(this.zone.designable?.rotate ?? 0) || 0; }

    /** px per mm on the artboard. The artboard resolution is arbitrary — what must stay
     *  constant is the PHYSICAL size a design comes out at. */
    pxPerMm() { return this.CW / this.zone.designable.width; }

    /** How big ONE design may be, in mm.
     *
     *  ⚠️ The hoop bounds an ELEMENT, never the whole design. Customers place designs in
     *  different spots on the same product and each is hooped and stitched separately, so
     *  every element costs its own hooping and is measured on its own — three 60mm names
     *  on a sweater are three hoopings, not one 180mm design that has to fit at once.
     *
     *  The hoop turns freely against the garment, so a horizontal run gets its long axis
     *  and the cap height the short one. Whichever of hoop and design area is smaller wins.
     */
    elementLimitMm() {
      const d = this.zone.designable;
      const h = this.zone.hoop;
      const long = h?.width && h?.height ? Math.max(h.width, h.height) : Infinity;
      const short = h?.width && h?.height ? Math.min(h.width, h.height) : Infinity;
      // FIT must match scripts/check_zone_scale.py — it derives zone.max_chars from this
      // same limit, and the two drifting apart is the whole failure this guards against.
      return { w: Math.min(d.width * FIT, long), h: Math.min(d.height * FIT, short) };
    }

    /** The scale this element's digitised artwork would actually be RUN at, or null if it
     *  has none to scale (a webfont, an uploaded design).
     *
     *  Mirrors pes_compose.pick_tier — nearest digitised size, then scaled to what was
     *  asked for. Below SAFE_SCALE the stitches pack tight enough to break thread and
     *  stiffen the cloth; above it they open into gaps. Neither is visible in a preview,
     *  so it has to be caught here rather than at the machine after the customer has paid.
     *
     *  A FACE-level check cannot answer this. The interlocking monogram is one height but
     *  57..139mm wide depending on which two letters interlock, so on a 3in napkin 334 of
     *  its 351 pairs are fine and 17 are not — which pair was chosen is the whole question. */
    stitchScale(e) {
      if (e.type === 'icon') {
        return e.iconMm?.h ? (e.size / this.pxPerMm()) / e.iconMm.h : null;
      }
      if (e.type !== 'text' || !e.font?.glyphs || !e.font.tiers?.length) return null;
      const mm = this.capMm(e);
      if (!mm) return null;
      const t = e.font.tiers.reduce((a, b) => (Math.abs(b.cap_mm - mm) < Math.abs(a.cap_mm - mm) ? b : a));
      return mm / t.cap_mm;
    }

    /** How wide `text` comes out, in real millimetres, at the smallest size this face can
     *  safely be run at. That size is the floor — below 0.8x of the smallest digitised file
     *  the stitches pack tight enough to break thread — so this is the most text that can
     *  ever fit, not merely how it looks right now. */
    textMmAtFloor(text, font, at) {
      const capMm = at || this.zone.min_design_mm || 12.7;
      const m = this.stitchMetrics({ type: 'text', value: text, font, size: capMm });
      return m.wMm * (capMm / font.cap_mm);
    }

    /** The longest PREFIX of `v` that can actually be stitched here.
     *
     *  Measured on the REAL text, not counted. Those are different questions on a
     *  proportional alphabet, and the difference is not small: on a 3in napkin Navidad
     *  fits WWW and no more, but LILI, TITI and ELLI all fit with millimetres to spare.
     *  A limit taken from the widest glyph is right for the worst name and needlessly
     *  refuses most real ones — which is how icons have always worked (each judged on its
     *  own size) and how lettering should have.
     *
     *  ⚠️ Not a substitute for maxCharsFor. That answers "what is guaranteed for ANY name
     *  of this length", which is what a zone spec can express and what the checks assert.
     *  This answers "does THIS name fit", which only the browser holding the text can. */
    fitText(v, font, at) {
      if (!font?.glyphs || !font.tiers?.length) return v.slice(0, this.zone.max_chars);
      if (font.pairs) return v.slice(0, 2);
      const lim = this.elementLimitMm().w;
      if (this.textMmAtFloor(v, font, at) <= lim) return v;
      for (let n = v.length - 1; n > 0; n -= 1) {
        if (this.textMmAtFloor(v.slice(0, n), font, at) <= lim) return v.slice(0, n);
      }
      return v.slice(0, 1);
    }

    /** The counter's denominator: how long THIS text could still grow, worst case. Recomputed
     *  as they type, so narrow letters visibly buy more room instead of silently not. */
    roomFor(e) {
      const f = e.font;
      if (!f?.glyphs || !f.tiers?.length) return this.zone.max_chars;
      if (f.pairs) return 2;
      const wide = Object.entries(f.glyphs)
        .reduce((a, [k, g]) => (g.w > (f.glyphs[a]?.w ?? -1) ? k : a), Object.keys(f.glyphs)[0]);
      return Math.max(e.value.length, this.fitText(e.value + wide.repeat(12), f).length);
    }

    /** Characters THIS face can set in this zone. Not zone.max_chars — that is only the
     *  ceiling, taken from the most permissive alphabet.
     *
     *  The binding constraint is stitch DENSITY, not a character count. pes_compose scales
     *  the nearest digitised tier to the size asked for, and below SAFE_SCALE (0.8x) the
     *  stitches pack tight enough to break thread and stiffen the fabric. So the real limit
     *  is how much fits at the smallest size an alphabet can safely run at — and that
     *  differs 1.7x between the faces on offer. One number per zone is wrong for at least
     *  one of them: a 3-inch napkin takes three Navidad letters or one Kendall.
     *
     *  Measured off the widest glyph, so the answer holds for any name of that length
     *  rather than only the one being typed. */
    maxCharsFor(font) {
      const ceiling = this.zone.max_chars;
      if (!font?.glyphs || !font.tiers?.length) return ceiling;   // vinyl: any real font
      const capMm = this.zone.min_design_mm || 12.7;
      const lim = this.elementLimitMm();
      // ZERO is a real answer: an alphabet whose smallest safe size is TALLER than the
      // design area cannot go on this product at all. Kendall's smallest digitised cap is
      // 63.8mm — 51mm at the density floor, taller than the bucket hat's 2in band. Checked
      // before width, because no character count rescues a letter that is too tall.
      if (capMm > lim.h) return 0;
      const wide = Object.entries(font.glyphs)
        .reduce((a, [k, g]) => (g.w > (font.glyphs[a]?.w ?? -1) ? k : a),
                Object.keys(font.glyphs)[0]);
      // A pair font is fixed at two initials, so the only question is whether the widest
      // ornament fits. Its size control sets the design's own HEIGHT, not a cap line, so
      // the width follows from that glyph's own proportions.
      if (font.pairs) {
        // The NARROWEST ornament, not the widest. These 351 pairs run 57..139mm wide at one
        // height, so judging the FACE by its worst one hid the whole monogram from napkins
        // when 334 of the 351 fit. Whether a PARTICULAR pair fits is settled per element by
        // stitchScale(), which is where a per-item question belongs.
        const g = Object.values(font.glyphs).reduce((a, b) => (b.w < a.w ? b : a));
        return g.w * (capMm / g.h) <= lim.w ? 2 : 0;
      }
      for (let n = ceiling; n > 1; n -= 1) {
        const m = this.stitchMetrics({ type: 'text', value: wide.repeat(n), font, size: capMm });
        if (m.wMm * (capMm / font.cap_mm) <= lim.w) return n;
      }
      return 1;
    }

    // ---------- stitched alphabets ----------
    /** Fonts the customer can pick. The system faces are placeholders that match nothing
     *  the machine makes; the stitched alphabets ARE the machine's letterforms, so on
     *  embroidery they are offered first and the placeholders stay as the escape hatch for
     *  text these A-Z-only alphabets cannot set. */
    /** Embroidery is stitched from a .PES file, so it offers ONLY the digitised alphabets.
     *  A webfont has no stitch file behind it: offering one would let a customer approve
     *  letterforms this shop cannot sew, and the failure surfaces at the machine after
     *  they have paid. Vinyl is cut from an outline, so any real font works there. */
    /** Drop the faces this zone has switched off, by catalogue key.
     *
     *  Excluded in the ZONE, not deleted from the catalogue, because that same catalogue is
     *  what scripts/pes_compose.py stitches from at fulfillment — deleting a face would
     *  make any order already placed in it unbuildable. Sport is out store-wide (smallest
     *  digitised cap 90mm, so a 5-letter name is 372mm — wider than the sweater's own
     *  panel); the sweater additionally drops the interlocking monogram. */
    offered(list) {
      const off = this.zone.exclude_fonts || [];
      // maxCharsFor, not just the exclude list. A face whose smallest safe size does not
      // fit this product must not be offered even if nobody remembered to exclude it —
      // the guard belongs where every caller already routes, so the spec being out of date
      // costs a missing chip rather than an unstitchable order.
      return list.filter((f) => !off.includes(f.key) && this.maxCharsFor(f) > 0);
    }

    fontsFor() {
      // A webfont is an OUTLINE with no stitch file behind it, so where it may be offered
      // is a per-face decision rather than a per-method rule: vinyl cuts any outline, but
      // embroidery only carries one the shop has agreed to digitise by hand. Declared in
      // the catalogue — absent means vinyl only, which is what Mod Chic has always been.
      const web = (this.webfonts || [])
        .filter((f) => (f.methods || ['vinyl']).includes(this.zone.method));
      // Pair fonts are deliberately NOT in this list. An interlocking monogram cannot set
      // a name, so offering it beside Navidad and Kendall would let a customer pick it and
      // watch "Maria" become "MA". It gets its own "+ Monogram" button instead.
      //
      // ⚠️ Stitched alphabets stay FIRST on embroidery. defaultFont() takes [0], so
      // leading with a hand-digitised face would drop every untouched order into the
      // manual queue without anyone choosing that.
      return this.offered(this.zone.method === 'embroidery'
        ? [...(this.stitched || []).filter((f) => !f.pairs), ...web]
        : [...web, ...FONTS]);
    }

    /** Interlocking monogram faces — two initials woven into one design. */
    pairFonts() { return this.offered((this.stitched || []).filter((f) => f.pairs)); }

    /** The faces THIS element may switch between. A monogram stays a monogram. */
    fontsForElement(e) {
      return e.font?.pairs ? this.pairFonts() : this.fontsFor();
    }

    /** The face a new design opens in. Not FONTS[0]: the catalogue arrives after the panel
     *  mounts, so a constant would default every new element to Arial and only the picker
     *  would show otherwise. The `|| FONTS[0]` is not cosmetic either — on embroidery the
     *  list is EMPTY until the alphabets arrive, and an element built with `font: undefined`
     *  throws on the first draw. `fontAuto` re-points it the moment they land.
     *
     *  ⚠️ Pinyon Script leads on embroidery (owner, 2026-08-11), in place of Christmas
     *  (`navidad`). It is a WEBFONT — there is no stitch file behind it — so an order left
     *  on the default now needs its lettering digitised by hand, which the face note under
     *  the picker says plainly. That is the trade the owner asked for; the stitched
     *  alphabets are still one chip away. Vinyl is untouched and keeps Mod Chic, the house
     *  face on 13 of the last 18 personalized orders. */
    defaultFont() {
      const list = this.fontsFor();
      const want = this.zone.default_font
        || (this.zone.method === 'embroidery' ? 'PBPinyon' : null);
      return (want && list.find((f) => f.key === want)) || list[0] || FONTS[0];
    }

    /** Load the shop's own faces before offering them.
     *
     *  Canvas does not wait for fonts: `ctx.font` with an unloaded family silently draws
     *  the fallback, so a customer would design in Times and see Mod Chic on reload. A
     *  face that fails to load is dropped from the picker rather than offered as a
     *  fallback — showing the wrong letterforms is the exact failure this replaced. */
    async loadWebfonts(list) {
      if (!list.length || !window.FontFace) return;
      const loaded = await Promise.all(list.map(async (w) => {
        if (!PersonalizerPanel.faces.has(w.family)) {
          const face = new FontFace(w.family, `url(${w.url})`);
          PersonalizerPanel.faces.set(
            w.family, face.load().then((f) => document.fonts.add(f)));
        }
        try {
          await PersonalizerPanel.faces.get(w.family);
        } catch (err) {
          console.error('[personalizer] font failed', w.family, err);
          return null;
        }
        // key and methods ride along: `key` is what offered(), the chip selection test and
        // the recipe's font_key all identify a face by, and the LABEL is not stable enough
        // to stand in for it. `methods` is where the face may be offered at all.
        return { key: w.key, label: w.label, methods: w.methods, css: `"${w.family}", serif` };
      }));
      this.webfonts = loaded.filter(Boolean);
    }

    /** The colours this product can actually be made in. */
    colorsFor() { return this.palette || THREADS; }

    /** Closest stocked colour to an arbitrary hex, compared in plain RGB distance —
     *  good enough to move a fallback swatch onto real stock. */
    nearestColor(hex) {
      const to = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
      const [r, g, b] = to(hex);
      return this.colorsFor().reduce((best, c) => {
        const [cr, cg, cb] = to(c.hex);
        const d = (cr - r) ** 2 + (cg - g) ** 2 + (cb - b) ** 2;
        return d < best.d ? { ...c, d } : best;
      }, { ...this.colorsFor()[0], d: Infinity });
    }

    /** Text reduced to what an alphabet can actually stitch. UPPERCASE A-Z and spaces —
     *  real orders have carried "Alexia S. Lugo" and "A.C.C", and the period simply does
     *  not exist as a glyph. Dropping it silently would be worse than saying so, which the
     *  editor does under the field. */
    /** family -> load promise. Shared across panels so two personalizers on one page
     *  fetch each face once. */
    static faces = new Map();

    static stitchable(s, font) {
      const up = String(s || '').toUpperCase();
      // An interlocking monogram is ONE design for TWO letters, so there is nothing to
      // typeset and nothing to drop character by character — just take the first two.
      if (font.pairs) return up.replace(/[^A-Z]/g, '').slice(0, 2);
      return up.split('').filter((c) => c === ' ' || font.glyphs[c]).join('');
    }

    /** Pairs are stored alphabetically, so the lookup key is the two letters sorted —
     *  "MA" and "AM" are the same interlocked design. */
    static pairKey(v) {
      const two = String(v || '').toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2);
      return two.length === 2 ? two.split('').sort().join('') : '';
    }

    /** The blocks this element's CURRENT glyph can actually be split into.
     *
     *  Declared per font, but honoured per GLYPH: Monogram Chic's 10 same-letter pairs are
     *  digitised as one block and 5 more are split into 3-5, so offering two thread
     *  pickers there would promise a two-tone monogram the file cannot produce. Those fall
     *  back to a single colour rather than guessing which block is which letter. */
    activeBlocks(e) {
      const b = this.blocksOf(e);
      if (!b) return null;
      const items = this.stitchMetrics(e).items;
      if (!items.length) return b;
      return items.every((it) => (it.g.cols || []).length === b.length) ? b : null;
    }

    /** The alphabet's own thread blocks, or null for a single-colour face.
     *  Sport is digitised as a varsity letter — fill, inner outline, outer outline — so
     *  it needs three thread pickers. Flattening it to one is what shipped, and it threw
     *  the whole font away: every letter previewed as a solid blob. */
    blocksOf(e) {
      return (e.type === 'text' && e.font?.blocks?.length > 1) ? e.font.blocks : null;
    }

    /** Thread per block. `e.color` stays the block-0 colour, so every single-colour path —
     *  icons, uploads, the layer badge, the nearest-stocked-colour repair — is untouched. */
    blockColors(e) {
      const b = this.activeBlocks(e);
      if (!b) return [e.color];
      return b.map((blk, i) => e.colors?.[i]
        || (i ? this.nearestColor(blk.rgb || '#000000').hex : e.color));
    }

    /** A multi-block alphabet opens in its OWN colourway, snapped to stocked thread.
     *  Defaulting every block to one colour would render Sport as the same solid blob
     *  this replaces, so the customer would never see what the font actually is. */
    seedBlockColors(e) {
      const b = this.activeBlocks(e);
      if (!b) { delete e.colors; return; }
      // A block with no digitised colour of its own (Monogram Chic picks a different
      // second thread in almost every file) keeps whatever is already selected.
      e.colors = b.map((blk, i) => (blk.rgb ? this.nearestColor(blk.rgb).hex
        : (e.colors?.[i] || e.color)));
      e.color = e.colors[0];
    }

    /** Cap height in REAL millimetres on the product — what the .PES has to come out at.
     *
     *  The recipe carries this rather than a size tier. The alphabets are digitised at
     *  fixed sizes, but the customer sizes freely, so scripts/pes_compose.py picks the
     *  nearest digitised size and scales to this figure. Recording millimetres instead of
     *  a tier keeps the recipe honest about what was actually approved on screen.
     *  ⚠️ e.size is ARTBOARD PIXELS and means nothing off this canvas. */
    capMm(e) {
      return +(e.size / this.pxPerMm()).toFixed(1);
    }

    /** Glyph image, cached, redrawing when it arrives. */
    glyphImg(url) {
      this.glyphCache ??= new Map();
      if (!this.glyphCache.has(url)) {
        const i = new Image();
        i.crossOrigin = 'anonymous';
        i.onload = () => this.draw();
        i.onerror = () => console.error('[personalizer] glyph failed', url);
        i.src = url;
        this.glyphCache.set(url, i);
      }
      return this.glyphCache.get(url);
    }

    /** Lay out one stitched word. Everything is measured in the alphabet's OWN
     *  millimetres and scaled once, so letters keep their real relative widths.
     *
     *  Glyphs align on the CAP LINE, not the bottom: these are all-caps alphabets whose
     *  letters share a cap height to within a millimetre, except descenders — Sport's Q is
     *  186mm against 166mm for everything else. Bottom-aligning would lift the Q by its
     *  own tail. */
    stitchMetrics(e) {
      const f = e.font;
      if (f.pairs) {
        // One design, looked up whole. There is no shared cap line to scale against —
        // these run 51..77mm tall depending on which two letters interlock — so the
        // design's OWN height is what the size control sets.
        const g = f.glyphs[PersonalizerPanel.pairKey(e.value)];
        if (!g) return { items: [], scale: 1, track: 0, wMm: 0 };
        return { items: [{ g, x: 0 }], scale: e.size / g.h, track: 0, wMm: g.w };
      }
      const scale = e.size / f.cap_mm;              // px per mm of this alphabet
      // Per font, defaulting to 0.06. NEGATIVE on the swash scripts: every glyph is
      // trimmed to its own ink, so butting the boxes stands a flourish beside its
      // neighbour rather than running it underneath, and Champagne set nine cap heights
      // wide for five letters. Must stay in step with TRACK in scripts/pes_compose.py —
      // that is what sews the word this previews.
      const track = f.cap_mm * (f.track ?? 0.06);
      const space = f.cap_mm * 0.34;
      const items = [];
      let x = 0;
      for (const ch of PersonalizerPanel.stitchable(e.value, f)) {
        if (ch === ' ') { x += space + track; continue; }
        const g = f.glyphs[ch];
        items.push({ g, x });
        x += g.w + track;
      }
      return { items, scale, track, wMm: Math.max(0, x - track) };
    }

    /** Starting size for a new design, in millimetres rather than pixels.
     *
     *  It used to be a flat 110px, which silently meant "18mm" only while every artboard
     *  happened to be 700 wide. Capping the longer side for the spine changed the book
     *  artboard to 486 wide and every design became 44% bigger in mm overnight. */
    defaultSize() { return Math.round(18 * this.pxPerMm()); }

    /** The size a design OPENS at.
     *
     *  18mm is a sensible default for a webfont, which has no fixed size, and wrong for
     *  everything that comes off a stitch file. An alphabet's smallest digitised cap is
     *  25.9mm for Navidad, 63.8mm for Kendall and 76.1mm for the interlocking monogram, so
     *  the shared 18mm default opened them at 0.70x, 0.28x and 0.24x — all below the
     *  density floor before the customer had done anything. The comment in add() has always
     *  claimed a stitched alphabet "opens at one of its real sizes"; only seedBlockColors
     *  was ever called, so it never did. Same fix as loadIconImage. */
    startSizeFor(font) {
      if (!font?.glyphs || !font.tiers?.length) return this.defaultSize();
      return Math.round(Math.min(...font.tiers.map((t) => t.cap_mm)) * this.pxPerMm());
    }

    /** Shrink text that cannot fit the design area, instead of letting it sit outside and
     *  gating Apply.
     *
     *  Not a nicety: a real order carried "Alexia S. Lugo", and at the starting size that
     *  is wider than the printable area. The customer types their own name and the Add
     *  button goes dead with nothing explaining why. Shrinking is what they meant; they
     *  can still enlarge it with the slider and see a proper warning if they overdo it. */
    /** The smallest this element may be drawn and still be sewable.
     *
     *  ⚠️ FITTING MUST STOP HERE. Shrinking past this does not make a design fit — it makes
     *  one that cannot be stitched, which is worse than one that visibly does not fit,
     *  because only the second is obvious. When even this floor is too big, the design is
     *  genuinely impossible and violations() says so instead of quietly producing it. */
    minSafePx(e) {
      // ⚠️ NOT 0.8x of the digitised size. That assumed the stitches get SCALED, which
      // packs them — the Soy Sauce icon is 1.52 stitches/mm² and scaling it to 0.5in would
      // be 153/mm². Re-spacing in Embird REGENERATES the fill, so the same design comes out
      // at ~123 stitches and sews fine; the owner has done exactly that. Any size is
      // reachable, so the floor is a shop capability (zone.min_design_mm) rather than a
      // property of the file. SAFE_SCALE still decides whether the design runs AS-IS, and
      // order-preview.py tells the person at the machine which case they have.
      const floor = this.zone.min_design_mm || 12.7;
      // e.size means CAP HEIGHT for text and OVERALL HEIGHT for an icon, so the floor
      // cannot be the same number for both. Lettering is bounded by cap height — that is
      // what legibility depends on. An icon is bounded by its LONGEST side: MiniRedHearts
      // is 98 x 11.3mm, and holding its height to 12.7mm forced a strip that fits easily
      // out to 110mm wide, i.e. off the product.
      if (e.type === 'icon' && e.iconMm?.w && e.iconMm?.h) {
        return Math.ceil(floor * (e.iconMm.h / Math.max(e.iconMm.w, e.iconMm.h)) * this.pxPerMm());
      }
      return Math.ceil(floor * this.pxPerMm());
    }

    /** The sizes this design can ACTUALLY be made at, in artboard px.
     *
     *  The slider used to run a hardcoded 30..260px, which is neither what the machine can
     *  sew nor what the product has room for. So a customer could shrink a name, watch
     *  space open up around it, and still not be allowed another letter — because the
     *  character limit is computed at the density floor, which they had already passed.
     *  The design area is meant to mirror the real product; a control that ranges outside
     *  what is makeable breaks that promise more than any drawing error.
     *
     *  Bounded by BOTH: stitch density (a digitised file may be run 0.8x..1.25x) and the
     *  room available (design area, and the hoop). Returns null when the size is not the
     *  customer's to choose. */

    sizeRangePx(e) {
      const px = this.pxPerMm();
      // Floor is what the shop will make; ceiling is what the product and the hoop allow.
      // No band arithmetic: re-spacing reaches any size in between.
      const lo = Math.max(12, this.minSafePx(e));
      let hi = Infinity;
      const b = this.bbox(e);
      if (b.w && b.h && e.size) {
        const lim = this.elementLimitMm();
        const rot = Math.abs((e.rot || 0) % 180);
        const turned = rot > 45 && rot < 135;
        hi = Math.min(hi, Math.floor(e.size * Math.min(((turned ? lim.h : lim.w) * px) / b.w,
                                                       ((turned ? lim.w : lim.h) * px) / b.h)));
      }
      if (!Number.isFinite(hi)) hi = lo * 8;
      return hi <= lo ? { min: lo, max: lo, only: true } : { min: lo, max: hi };
    }


    shrinkToFit(e) {
      if (e.type !== 'text' || !e.value) return;
      const rot = Math.abs((e.rot || 0) % 180);
      const along = rot > 45 && rot < 135 ? this.CH : this.CW;
      const floor = Math.max(12, this.minSafePx(e));
      for (let i = 0; i < 24 && this.bbox(e).w > along * 0.96 && e.size > floor; i += 1) {
        e.size = Math.max(floor, Math.round(e.size * 0.92));
      }
    }

    /** Shrink ANY element that will not hoop.
     *
     *  Two ceilings, not one. The design AREA says where a design may sit; the HOOP says how
     *  big a single stitch-out may be, and on a sweater the hoop is the smaller by a factor
     *  of two. Bounding only by the artboard let a 350mm design through on a machine that
     *  cannot hoop past 203mm.
     *
     *  ⚠️ Type-agnostic on purpose. This lived inside shrinkToFit, which returns early on
     *  anything that is not text, so icons were never capped at all — an icon could be
     *  dragged to the full 381mm sweater panel and no check anywhere would object.
     *  violations() bounds-checks placement, not size, so nothing else caught it. */
    clampToLimit(e) {
      const b = this.bbox(e);
      if (!b.w || !b.h) return;
      const turned = (() => { const r = Math.abs((e.rot || 0) % 180); return r > 45 && r < 135; })();
      const lim = this.elementLimitMm();
      const maxW = (turned ? lim.h : lim.w) * this.pxPerMm();
      const maxH = (turned ? lim.w : lim.h) * this.pxPerMm();
      const k = Math.min(maxW / b.w, maxH / b.h, 1);
      if (k < 1) e.size = Math.max(Math.max(12, this.minSafePx(e)), Math.floor(e.size * k));
    }

    /** Size text to span the design area instead of letting the customer pick.
     *
     *  A spine is a fixed strip: "AEY" and "JANINA BENCHIMOL" cannot share a type size and
     *  both look right, and a size slider on a 13mm strip only offers ways to get it wrong.
     *  So on an autofit zone the size follows the name's length.
     *
     *  Measured, not estimated from character count — the fonts here are proportional and
     *  a script face like Snell is far narrower than Futura at the same px.
     */
    fitSize(e) {
      if (!this.zone.autofit || e.type !== 'text' || !e.value) return e.size;
      // Rotated 90°, the text runs along the artboard's LONG axis and its height is
      // limited by the short one.
      const along = this.startRot() % 180 ? this.CH : this.CW;
      const across = this.startRot() % 180 ? this.CW : this.CH;
      const probe = 100;
      // A stitched alphabet has no CSS family to measure with — its width comes from the
      // glyph table. Measuring it as `100px undefined` silently returns a browser default.
      let w;
      if (e.font?.glyphs) {
        w = this.stitchMetrics({ ...e, size: probe }).wMm * (probe / e.font.cap_mm);
      } else {
        this.ctx.font = `${probe}px ${e.font.css}`;
        w = this.ctx.measureText(e.value).width;
      }
      w = w || 1;
      const byLength = (along * 0.92) / (w / probe);
      const byHeight = across * 0.62;              // cap height, not the em box
      return Math.max(24, Math.min(byLength, byHeight));
    }

    /** Apply autofit to every text element. Called before drawing and before saving, so
     *  the preview, the recipe and the printed artwork cannot disagree. */
    applyFit() {
      for (const side of this.sides) {
        for (const e of this.design[side] || []) {
          if (e.type === 'text') {
            if (this.zone.autofit) e.size = Math.round(this.fitSize(e));
            else this.shrinkToFit(e);
          }
          // EVERY type, text or not. The hoop is a property of the machine, not of
          // lettering, so an icon or an uploaded design has to clear it too.
          this.clampToLimit(e);
        }
      }
    }

    /** Add-to-cart, when the design costs something.
     *
     *  A theme cannot invent a charge, so extra designs and uploads ride as quantities of
     *  the unlisted `Personalization` product. Everything goes in ONE /cart/add.js call,
     *  so a customer can never end up holding add-on lines for an item that failed to add.
     *
     *  The listener is on `document` in CAPTURE phase on purpose. Dawn's product-form
     *  binds submit on the form ELEMENT, and listeners on the target itself run in
     *  registration order regardless of capture — binding to the form would run second and
     *  stopImmediatePropagation would be too late. */
    wireCart() {
      if (this.cartWired || !this.form) return;
      this.cartWired = true;
      document.addEventListener('submit', (ev) => {
        if (ev.target !== this.form) return;
        const c = this.charges();
        // A design the customer WORKED ON but that cannot be made must not slip through as
        // a plain item — that is the same silent loss auto-apply exists to stop, reached by
        // a different door. Open the editor on the problem instead of adding a blank one.
        if (!this.applied && this.touched && this.allEls().length) {
          ev.preventDefault();
          ev.stopImmediatePropagation();
          // Reuses the toggle's own handler rather than repeating what opening involves.
          if (this.q('body')?.hidden) this.q('open')?.click();
          this.saveUI();
          this.stageEl?.scrollIntoView({ block: 'center', behavior: 'smooth' });
          return;
        }
        // Intercept whenever there is a DESIGN, not only when there is a charge. A single
        // spine name is free, and letting Dawn submit it natively meant the order got no
        // rendered preview and no _design_id — on the one product where the PNG is the
        // only visual, because the spine has no preview at all.
        if (!this.applied || !this.allEls().length) return;
        // Something is chargeable but the zone has no variant to charge it through: let
        // Dawn add the item normally rather than take the design and lose the money.
        if (c.total && !c.lines.length) return;
        ev.preventDefault();
        ev.stopImmediatePropagation();
        this.addToCart(c).catch((e) => {
          this.q('savenote').innerHTML =
            `<span class="pz__warn">Could not add to cart: ${esc(e.message)}</span>`;
        });
      }, true);
    }

    async addToCart(c) {
      const fd = new FormData(this.form);
      const props = {};
      for (const [k, v] of fd.entries()) {
        const m = /^properties\[(.+)\]$/.exec(k);
        if (m && v !== '' && !(v instanceof File)) props[m[1]] = v;
      }
      // A shared token, not the parent's line key: the key does not exist until after the
      // add, and one POST is worth more than a tidier reference. The packing slip matches
      // add-on lines to their item by this.
      const token = `pz-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e4)}`;
      props._design_id = token;
      // multipart, NOT JSON. Customer artwork is a real File and JSON cannot carry one —
      // the earlier JSON body silently dropped every upload. Verified that /cart/add.js
      // accepts items[N][...] encoding WITH a file property in one request, so the parent
      // and its add-on lines still go in a single call.
      const body = new FormData();
      let n = 0;
      const put = (k, v, name) => (name ? body.append(`items[${n}][${k}]`, v, name)
                                        : body.append(`items[${n}][${k}]`, v));
      put('id', Number(fd.get('id')));
      put('quantity', Number(fd.get('quantity') || 1));
      for (const [k, v] of Object.entries(props)) put(`properties[${k}]`, v);
      // One property per file, so each lands on the order as its own named link.
      const files = this.allEls().filter((e) => e.type === 'upload' && e.file);
      files.forEach((e, i) => put(
        `properties[${files.length > 1 ? `Artwork ${i + 1}` : 'Artwork'}]`, e.file, e.file.name));
      // Two pictures per side, because they answer different questions.
      // _Preview: the design ON the product, as the customer saw it — WHERE they put it.
      // _Artwork: the same design alone on transparency, 1:1 with the design area — the
      //           file to cut or stitch from.
      // Leading underscore on both: production wants them, the customer does not need CDN
      // links cluttering their cart.
      for (const [side, blob] of await this.compositeBlobs()) {
        put(`properties[_Preview ${side}]`, blob, `preview-${side}.jpg`);
      }
      for (const [side, blob] of await this.previewBlobs()) {
        put(`properties[_Artwork ${side}]`, blob, `artwork-${side}.png`);
      }
      // One cart line per charge, all in this SAME request as the parent, so a customer
      // can never be left holding add-on lines for an item that failed to add.
      for (const l of c.lines) {
        n += 1; put('id', l.id); put('quantity', l.qty);
        put('properties[_design_for]', token);
      }

      const cart = document.querySelector('cart-notification')
        || document.querySelector('cart-drawer');
      if (cart) {
        body.append('sections', cart.getSectionsToRender().map((s) => s.id));
        body.append('sections_url', window.location.pathname);
      }
      const btn = this.form.querySelector('[type="submit"]');
      if (btn) btn.setAttribute('aria-disabled', 'true');
      try {
        // No Content-Type header: the browser has to set the multipart boundary itself.
        const r = await fetch(`${window.routes.cart_add_url}`, {
          method: 'POST', headers: { Accept: 'application/javascript' }, body,
        });
        // Not always JSON. Shopify answers with an HTML bot-check page under rate limiting
        // (seen as a real 429 during testing), and a JSON parse error is not something to
        // show a customer.
        const raw = await r.text();
        let res;
        try {
          res = JSON.parse(raw);
        } catch {
          throw new Error(r.status === 429 || r.status === 430
            ? 'the shop is busy right now — please try again in a moment'
            : `the cart did not respond properly (${r.status})`);
        }
        if (res.status) throw new Error(res.description || res.message || 'rejected');
        if (cart) {
          cart.renderContents(res);
        } else {
          window.location = window.routes.cart_url;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate,
                  { source: 'personalizer', productVariantId: fd.get('id') });
        }
      } finally {
        if (btn) btn.removeAttribute('aria-disabled');
      }
    }

    /** Axis-aligned bounds AFTER rotation — the upright box would let a tilted design
     *  poke outside the print area at its corners. */
    rectOf(e, x = e.x, y = e.y) {
      const b = this.bbox(e);
      const a = (e.rot || 0) * Math.PI / 180;
      const c = Math.abs(Math.cos(a));
      const s = Math.abs(Math.sin(a));
      const w = (b.w * c + b.h * s) / 2;
      const h = (b.w * s + b.h * c) / 2;
      return { x0: x - w, x1: x + w, y0: y - h, y1: y + h };
    }

    gapPx() { return this.zone.min_gap / this.zone.designable.width * this.CW; }

    polyPts() {
      return (this.zone.designable.polygon || []).map(([x, y]) => [x * this.CW, y * this.CH]);
    }

    /** Ray casting. Off-canvas is out regardless, so bounds and shape share one test. */
    allowed(x, y) {
      if (x < 0 || y < 0 || x > this.CW || y > this.CH) return false;
      const pts = this.polyPts();
      if (pts.length < 3) return true;
      let inside = false;
      for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
        const [xi, yi] = pts[i];
        const [xj, yj] = pts[j];
        if ((yi > y) !== (yj > y) && x < (xj - xi) * (y - yi) / (yj - yi) + xi) inside = !inside;
      }
      return inside;
    }

    inBox(e, x, y) {
      const r = this.rectOf(e, x, y);
      if (r.x0 < 0 || r.y0 < 0 || r.x1 > this.CW || r.y1 > this.CH) return false;
      const mx = (r.x0 + r.x1) / 2;
      const my = (r.y0 + r.y1) / 2;
      for (const [px, py] of [[r.x0, r.y0], [r.x1, r.y0], [r.x0, r.y1], [r.x1, r.y1],
        [mx, r.y0], [mx, r.y1], [r.x0, my], [r.x1, my]]) {
        if (!this.allowed(px, py)) return false;
      }
      return true;
    }

    clearOf(e, x, y) {
      const r = this.rectOf(e, x, y);
      const g = this.gapPx();
      return this.els.every((o) => {
        if (o === e) return true;
        const q = this.rectOf(o);
        return r.x1 + g <= q.x0 || r.x0 - g >= q.x1 || r.y1 + g <= q.y0 || r.y0 - g >= q.y1;
      });
    }

    /** Every side, not just the visible one — Save gates on the whole design. */
    violations(side = this.side) {
      const prev = this.side;
      this.side = side;
      const out = [];
      for (const e of this.els) {
        if (!this.inBox(e, e.x, e.y)) out.push({ e, side, why: 'outside the design area' });
        else if (!this.clearOf(e, e.x, e.y)) {
          out.push({ e, side, why: `closer than ${this.zone.min_gap}mm to another element` });
        }
        // ⚠️ Stitch density is deliberately NOT checked here. It briefly gated Save, and
        // that refused work this shop actually does: outside SAFE_SCALE a design needs
        // re-spacing in Embird before it runs, which is a production step rather than an
        // impossibility — the owner has sewn a 127mm icon at 0.5in. The cost belongs with
        // the person at the machine, who can see it (order-preview.py prints the scale and
        // the warning), not with a customer who cannot. What the customer IS held to is
        // room: min_design_mm at the bottom, the design area and hoop at the top.
      }
      this.side = prev;
      return out;
    }

    allViolations() { return this.sides.flatMap((s) => this.violations(s)); }

    mmOf(e) {
      const z = this.zone.designable;
      return {
        x: +(e.x / this.CW * z.width - z.width / 2).toFixed(1),
        y: +(z.height / 2 - e.y / this.CH * z.height).toFixed(1),
      };
    }

    // ---------- drawing ----------
    /** Paint a set of elements onto any context. Split out of drawArt so a side that is
     *  NOT on screen can still be rendered — the order needs a picture of the back even
     *  when the customer left the editor looking at the front. */
    paint(g, els) {
      for (const e of els) {
        g.save();
        g.translate(e.x, e.y);
        g.rotate((e.rot || 0) * Math.PI / 180);
        if (e.img) {
          const b = this.bbox(e);
          g.drawImage(e.img, -b.w / 2, -b.h / 2, b.w, b.h);
        } else if (e.font?.glyphs) {
          this.paintStitched(g, e);
        } else {
          // Text AND symbols: a symbol is a glyph, so it draws exactly like text and
          // inherits the chosen colour for free. Icons that carry an image took the
          // branch above; one with neither image nor value simply draws nothing.
          g.fillStyle = e.color;
          g.font = this.fontFor(e);
          g.textAlign = 'center';
          g.textBaseline = 'middle';
          g.fillText(e.value, 0, 0);
        }
        g.restore();
      }
    }

    /** Draw a stitched word, tinted to the chosen thread.
     *
     *  The glyph PNGs carry the colour they were digitised in — Kendall is electric blue —
     *  so drawing them raw would show the customer blue thread whatever they picked.
     *  Each glyph is stencilled through `source-in` on a scratch canvas so only its shape
     *  survives and the thread colour fills it. */
    paintStitched(g, e) {
      const m = this.stitchMetrics(e);
      if (!m.items.length) return;
      this.stencil ??= document.createElement('canvas');
      const sc = this.stencil;
      const sx = sc.getContext('2d');
      // Positions come from the layout pass, which already advanced the cursor past
      // spaces. Re-deriving x here by summing glyph widths would silently swallow them.
      const left = -(m.wMm * m.scale) / 2;
      const top = -e.size / 2;
      const blocks = this.activeBlocks(e);
      const cols = this.blockColors(e);
      for (const it of m.items) {
        const img = this.glyphImg(it.g.url);
        const w = Math.max(1, Math.round(it.g.w * m.scale));
        const h = Math.max(1, Math.round(it.g.h * m.scale));
        if (!img.complete || !img.naturalWidth) continue;
        // Cap line, so a descender hangs below instead of lifting the letter.
        // Recolour block by block only where THIS glyph carries a colour per block.
        if (blocks && (it.g.cols || []).length === blocks.length) {
          g.drawImage(this.blockGlyph(img, w, h, it.g.cols, cols),
                      left + it.x * m.scale, top, w, h);
          continue;
        }
        sc.width = w;
        sc.height = h;
        sx.clearRect(0, 0, w, h);
        sx.drawImage(img, 0, 0, w, h);
        sx.globalCompositeOperation = 'source-in';
        sx.fillStyle = e.color;
        sx.fillRect(0, 0, w, h);
        sx.globalCompositeOperation = 'source-over';
        g.drawImage(sc, left + it.x * m.scale, top, w, h);
      }
    }

    /** One glyph of a multi-block alphabet, repainted block by block.
     *
     *  Every source pixel is matched to the colour that block was DIGITISED in and
     *  repainted with the thread the customer picked for it.
     *  ⚠️ Matched on COLOUR, never on block index. Sport's tier-3 Z has its two outlines
     *  swapped relative to the other 25 letters, so an index-based recolour would invert
     *  that one letter and nothing would throw.
     *
     *  Cached at the DRAWN size rather than natural size: a Sport glyph is 1274x1660, and
     *  recolouring five of those per redraw is ~10M pixels. Keys are (glyph, drawn size,
     *  colours) and size is snapped to the alphabet's real tiers, so the cache cannot
     *  grow with slider movement. */
    blockGlyph(img, w, h, srcCols, cols) {
      this.blockCache ??= new Map();
      const key = `${img.src}|${w}x${h}|${cols.join(',')}`;
      if (this.blockCache.has(key)) return this.blockCache.get(key);

      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      const x = c.getContext('2d', { willReadFrequently: true });
      x.drawImage(img, 0, 0, w, h);
      const d = x.getImageData(0, 0, w, h);
      const p = d.data;
      const src = srcCols.map((c) => hexToRgb(c) || [0, 0, 0]);
      const dst = cols.map((v) => hexToRgb(v) || [0, 0, 0]);
      for (let i = 0; i < p.length; i += 4) {
        if (!p[i + 3]) continue;                 // fully transparent: nothing to recolour
        let bi = 0;
        let bd = Infinity;
        for (let k = 0; k < src.length; k += 1) {
          const dd = (p[i] - src[k][0]) ** 2 + (p[i + 1] - src[k][1]) ** 2
                   + (p[i + 2] - src[k][2]) ** 2;
          if (dd < bd) { bd = dd; bi = k; }
        }
        [p[i], p[i + 1], p[i + 2]] = dst[bi];
      }
      x.putImageData(d, 0, 0);
      if (this.blockCache.size > 200) this.blockCache.clear();
      this.blockCache.set(key, c);
      return c;
    }

    /** The product's own colour immediately AROUND the design area.
     *
     *  Every hardcover spine photograph in the catalogue is of an already-personalized
     *  book, so a customer's name would land on top of somebody else's. Painting the area
     *  out with the spine's own colour first is what makes those photos usable at all.
     *  Sampled from the band just outside the rect along the LONG axis, where the spine
     *  continues clean, so it matches the lighting instead of guessing a flat swatch. */
    sampleAround(g, l, t, r, b) {
      const w = r - l;
      const h = b - t;
      const bands = h > w
        ? [[l, t - h * 0.14, w, h * 0.10], [l, b + h * 0.04, w, h * 0.10]]
        : [[l - w * 0.14, t, w * 0.10, h], [r + w * 0.04, t, w * 0.10, h]];
      const px = [];
      for (const [x, y, pw, ph] of bands) {
        const X = Math.round(x);
        const Y = Math.round(y);
        const W = Math.round(pw);
        const H = Math.round(ph);
        if (W < 1 || H < 1 || X < 0 || Y < 0
            || X + W > g.canvas.width || Y + H > g.canvas.height) continue;
        const d = g.getImageData(X, Y, W, H).data;
        for (let i = 0; i < d.length; i += 4) px.push([d[i], d[i + 1], d[i + 2]]);
      }
      if (!px.length) return null;
      const med = (k) => px.map((q) => q[k]).sort((a, c) => a - c)[px.length >> 1];
      return `rgb(${med(0)},${med(1)},${med(2)})`;
    }

    /** Put the artwork into its place in a product photo.
     *
     *  `shot.cover` paints the area out first (the photo already carries a design), and
     *  `shot.rotate` turns the artwork for a photo shot at a different angle — the Birds
     *  spine lies HORIZONTALLY while the Flower one stands vertical, and the artboard is
     *  portrait for both. Without it a spine name would be squashed across the strip. */
    placeArt(g, art, shot, k = 1) {
      if (shot.quad?.length === 4) return this.placeArtQuad(g, art, shot, k);
      const [l, t, r, b] = shot.rect.map((v) => v * k);
      if (shot.cover) {
        const fill = this.sampleAround(g, l, t, r, b);
        if (fill) {
          g.fillStyle = fill;
          g.fillRect(l, t, r - l, b - t);
        }
      }
      const rot = +shot.rotate || 0;
      if (!rot) {
        g.drawImage(art, 0, 0, this.CW, this.CH, l, t, r - l, b - t);
        return;
      }
      g.save();
      g.translate((l + r) / 2, (t + b) / 2);
      g.rotate((rot * Math.PI) / 180);
      // A quarter turn swaps which side of the rect the artboard spans.
      const quarter = Math.abs(rot % 180) === 90;
      const w = quarter ? b - t : r - l;
      const h = quarter ? r - l : b - t;
      g.drawImage(art, 0, 0, this.CW, this.CH, -w / 2, -h / 2, w, h);
      g.restore();
    }

    /** Map the artboard onto an arbitrary QUADRILATERAL.
     *
     *  A rectangle can only describe a surface photographed square-on. The hardcover spine
     *  is shot at three-quarters, so it is a slanted band — which is why an axis-aligned
     *  design area could never sit on it, and why the name has to be mapped by its four
     *  corners instead.
     *
     *  Canvas 2D has no perspective transform, so the artboard is sliced along its length
     *  and each slice drawn with its own affine transform, interpolated between the quad's
     *  two long edges. On a narrow band the residual error is well under a pixel. Slices
     *  overlap by one pixel because adjacent affine transforms otherwise leave hairlines.
     *
     *  Corners are [top-left, top-right, bottom-right, bottom-left] of the DESIGN AREA as
     *  it appears in the photograph, in that photo's pixels. */
    placeArtQuad(g, art, shot, k = 1) {
      let corners = shot.quad.map((p) => [p[0] * k, p[1] * k]);
      // ORIENT THE QUAD, do not trust the corner order. The artboard's WIDTH must map to
      // the band's short side; if the corners were dragged starting from the other end —
      // which is the natural thing to do on a spine that runs across the photo rather than
      // down it — the artwork lands sideways and stretched. Rotating the order by one is
      // the whole fix, and it spares whoever calibrates from having to know the rule.
      const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);
      if (dist(corners[0], corners[1]) > dist(corners[0], corners[3])) {
        corners = [corners[1], corners[2], corners[3], corners[0]];
      }
      // Which END the text starts from cannot be inferred — dragging the corners from the
      // far end is just as natural and reads upside down. One explicit toggle beats a
      // guess that is wrong half the time.
      if (shot.flip) corners = [corners[2], corners[3], corners[0], corners[1]];
      const [tl, tr, br, bl] = corners;
      if (shot.cover) {
        const fill = this.sampleInsideQuad(g, [tl, tr, br, bl]);
        if (fill) {
          g.save();
          g.beginPath();
          g.moveTo(tl[0], tl[1]);
          g.lineTo(tr[0], tr[1]);
          g.lineTo(br[0], br[1]);
          g.lineTo(bl[0], bl[1]);
          g.closePath();
          g.fillStyle = fill;
          g.fill();
          g.restore();
        }
      }
      const lerp = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
      const N = 96;
      g.save();
      for (let i = 0; i < N; i += 1) {
        const t0 = i / N;
        const t1 = (i + 1) / N;
        const y0 = t0 * this.CH;
        const y1 = t1 * this.CH;
        const L0 = lerp(tl, bl, t0);
        const R0 = lerp(tr, br, t0);
        const L1 = lerp(tl, bl, t1);
        const ux = (R0[0] - L0[0]) / this.CW;
        const uy = (R0[1] - L0[1]) / this.CW;
        const vx = (L1[0] - L0[0]) / (y1 - y0);
        const vy = (L1[1] - L0[1]) / (y1 - y0);
        g.setTransform(ux, uy, vx, vy,
                       L0[0] - vx * y0, L0[1] - vy * y0);
        g.drawImage(art, 0, y0, this.CW, (y1 - y0) + 1,
                    0, y0, this.CW, (y1 - y0) + 1);
      }
      g.setTransform(1, 0, 0, 1, 0, 0);
      g.restore();
    }

    /** The product's own colour, sampled INSIDE the quad near its two short ends.
     *
     *  Sampling outside was wrong: a spine is a narrow band on a small object, so a step
     *  outward lands on the backdrop and painted a BEIGE block over a blue spine. The ends
     *  of the band are the same material as the middle and carry no lettering, so they are
     *  both safe and correct. */
    sampleInsideQuad(g, [tl, tr, br, bl]) {
      const lerp = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
      const px = [];
      for (const t of [0.04, 0.08, 0.92, 0.96]) {
        const L = lerp(tl, bl, t);
        const R = lerp(tr, br, t);
        for (const u of [0.3, 0.5, 0.7]) {
          const p = lerp(L, R, u);
          const x = Math.round(p[0]);
          const y = Math.round(p[1]);
          if (x < 1 || y < 1 || x >= g.canvas.width - 1 || y >= g.canvas.height - 1) continue;
          const d = g.getImageData(x - 1, y - 1, 3, 3).data;
          for (let i = 0; i < d.length; i += 4) px.push([d[i], d[i + 1], d[i + 2]]);
        }
      }
      if (!px.length) return null;
      const med = (c) => px.map((q) => q[c]).sort((a, b) => a - b)[px.length >> 1];
      return `rgb(${med(0)},${med(1)},${med(2)})`;
    }

    /** Kept for rect-based photos. */
    sampleAroundQuad(g, [tl, tr, br, bl]) {
      const mid = (a, b) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
      // Step perpendicular to each long edge, outward, and read a few points.
      const left = [tl, bl];
      const right = [tr, br];
      const nx = mid(tr, br)[0] - mid(tl, bl)[0];
      const ny = mid(tr, br)[1] - mid(tl, bl)[1];
      const len = Math.hypot(nx, ny) || 1;
      const off = Math.max(3, len * 0.22);
      const px = [];
      for (const [edge, sign] of [[left, -1], [right, 1]]) {
        for (let t = 0.15; t <= 0.85; t += 0.1) {
          const p = [edge[0][0] + (edge[1][0] - edge[0][0]) * t,
                     edge[0][1] + (edge[1][1] - edge[0][1]) * t];
          const x = Math.round(p[0] + (nx / len) * off * sign);
          const y = Math.round(p[1] + (ny / len) * off * sign);
          if (x < 1 || y < 1 || x >= g.canvas.width - 1 || y >= g.canvas.height - 1) continue;
          const d = g.getImageData(x - 1, y - 1, 3, 3).data;
          for (let i = 0; i < d.length; i += 4) px.push([d[i], d[i + 1], d[i + 2]]);
        }
      }
      if (!px.length) return null;
      const med = (c) => px.map((q) => q[c]).sort((a, b) => a - b)[px.length >> 1];
      return `rgb(${med(0)},${med(1)},${med(2)})`;
    }

    /** Artwork only — this is what gets printed, so no guides ever go here. */
    drawArt() {
      this.actx.clearRect(0, 0, this.CW, this.CH);
      this.paint(this.actx, this.els);
    }

    /** A PNG per personalized side, attached to the order.
     *
     *  The recipe already determines the design completely, so this is not the source of
     *  truth — it is for the person at the machine, who should not have to run a script to
     *  see what they are making. The canvas maps 1:1 onto the designable area, so the file
     *  is also directly usable as artwork: transparent background, exact placement.
     *
     *  Rendered on its OWN canvas, never `this.art` — that one is the live 3D texture and
     *  borrowing it mid-render is a race for the sake of saving eight lines. */
    /** Every image any side's design needs, actually loaded.
     *
     *  ⚠️ paintStitched SKIPS a glyph whose image has not arrived. That is right while
     *  editing — onload triggers a redraw — and badly wrong for a ONE-SHOT export. A side
     *  the customer never switched to has never had its glyphs requested, so the first
     *  export to run for it renders NOTHING: the back came out as a bare product photo
     *  while the front was perfect, and WHICH file lost depended on which generator
     *  happened to run first. Both now wait.
     *
     *  Errors resolve rather than reject: a missing glyph must not block an order. */
    async awaitArt() {
      const waits = [];
      const need = (img) => {
        if (!img || (img.complete && img.naturalWidth)) return;
        waits.push(new Promise((res) => {
          img.addEventListener('load', res, { once: true });
          img.addEventListener('error', res, { once: true });
        }));
      };
      for (const side of this.sides) {
        for (const e of this.design[side] || []) {
          need(e.img);                                    // icons and uploaded artwork
          if (e.type === 'text' && e.font?.glyphs) {
            for (const it of this.stitchMetrics(e).items) need(this.glyphImg(it.g.url));
          }
        }
      }
      if (waits.length) await Promise.all(waits);
    }

    async previewBlobs() {
      await this.awaitArt();
      const c = document.createElement('canvas');
      c.width = this.CW;
      c.height = this.CH;
      const g = c.getContext('2d');
      const out = [];
      for (const side of this.sides) {
        const els = this.design[side] || [];
        if (!els.length) continue;
        g.clearRect(0, 0, this.CW, this.CH);
        this.paint(g, els);
        const blob = await new Promise((r) => c.toBlob(r, 'image/png'));
        if (blob) out.push([side, blob]);
      }
      return out;
    }

    /** The design ON THE PRODUCT, as the customer saw it — one per personalized side.
     *
     *  The artwork-only PNG is what the cutter needs, but it is a name floating on
     *  transparency: it cannot tell whoever makes the order WHERE on the product the
     *  customer put it, which is half of what they chose. This is the composite.
     *
     *  Loads each side's own photo rather than reusing this.photoImg, because that one
     *  holds whichever side happened to be on screen when Apply was pressed. */
    async compositeBlobs() {
      if (this.noPreview) return [];          // no honest photo to composite onto
      await this.awaitArt();
      const out = [];
      const art = document.createElement('canvas');
      art.width = this.CW;
      art.height = this.CH;
      const actx = art.getContext('2d');
      for (const side of this.sides) {
        const els = this.design[side] || [];
        if (!els.length) continue;
        const shot = this.photoFor(side);
        if (!shot?.image || !shot.rect) continue;
        let img;
        try {
          img = await this.loadImage(shot.image);
        } catch {
          continue;                            // a missing photo must not block the order
        }
        // Downscaled, and JPEG. At full resolution this is a 2950x3688 PNG — 7.2MB per
        // side, uploaded while the customer waits at add-to-cart. It is a reference photo
        // for whoever makes the order, not a print master; the artwork PNG alongside it is
        // the file with the real detail, and that one stays lossless.
        const MAX = 1400;
        const k = Math.min(1, MAX / Math.max(img.naturalWidth, img.naturalHeight));
        const c = document.createElement('canvas');
        c.width = Math.round(img.naturalWidth * k);
        c.height = Math.round(img.naturalHeight * k);
        const g = c.getContext('2d');
        g.drawImage(img, 0, 0, c.width, c.height);
        actx.clearRect(0, 0, this.CW, this.CH);
        this.paint(actx, els);
        this.placeArt(g, art, shot, k);
        const blob = await new Promise((res) => c.toBlob(res, 'image/jpeg', 0.86));
        if (blob) out.push([side, blob]);
      }
      return out;
    }

    /** The calibrated photo for one side of the CURRENT colourway. */
    photoFor(side) {
      const way = this.way || {};
      return (side !== 'front' && (way[`photo_${side}`] || this.zone[`photo_${side}`]))
        || way.photo || this.zone.photo;
    }

    loadImage(src) {
      return new Promise((res, rej) => {
        const i = new Image();
        i.crossOrigin = 'anonymous';        // or toBlob throws on a tainted canvas
        i.onload = () => res(i);
        i.onerror = () => rej(new Error(`image failed: ${src}`));
        i.src = src;
      });
    }

    drawScrim(g, sx, sy, ox, oy, w = this.CW, h = this.CH) {
      const pts = this.polyPts();
      if (pts.length < 3) return;
      g.save();
      g.beginPath();
      g.rect(0, 0, w, h);
      pts.forEach(([x, y], i) => {
        const px = ox + x * sx;
        const py = oy + y * sy;
        i ? g.lineTo(px, py) : g.moveTo(px, py);
      });
      g.closePath();
      g.fillStyle = 'rgba(20,20,20,0.30)';
      g.fill('evenodd');
      g.restore();
    }

    drawGuides(g, sx = 1, sy = 1, ox = 0, oy = 0) {
      const X = (x) => ox + x * sx;
      const Y = (y) => oy + y * sy;
      g.save();
      g.strokeStyle = '#9a9384';
      g.setLineDash([9, 7]);
      g.lineWidth = 2;
      const o = this.polyPts();
      g.beginPath();
      if (o.length >= 3) o.forEach(([x, y], i) => (i ? g.lineTo(X(x), Y(y)) : g.moveTo(X(x), Y(y))));
      else g.rect(X(2), Y(2), (this.CW - 4) * sx, (this.CH - 4) * sy);
      g.closePath();
      g.stroke();
      g.restore();

      for (const e of this.els) {
        const bad = !this.inBox(e, e.x, e.y) || !this.clearOf(e, e.x, e.y);
        if (e.id !== this.sel && !bad) continue;
        const b = this.bbox(e);
        g.save();
        g.translate(X(e.x), Y(e.y));
        g.rotate((e.rot || 0) * Math.PI / 180);
        g.strokeStyle = bad ? '#c0392b' : '#2f6b53';
        g.lineWidth = 2;
        g.setLineDash([4, 4]);
        g.strokeRect((-b.w / 2 - 8) * sx, (-b.h / 2 - 8) * sy, (b.w + 16) * sx, (b.h + 16) * sy);
        g.restore();
      }
    }

    draw() {
      this.drawArt();
      if (this.noPreview) {
        // No stage to paint, but validation still has to run: max_chars and the bounds
        // check are what gate Save, and they are not display concerns.
        this.report();
        this.invalidateIfChanged();
        return;
      }
      this.ctx.clearRect(0, 0, this.CW, this.CH);
      this.drawScrim(this.ctx, 1, 1, 0, 0);
      this.ctx.drawImage(this.art, 0, 0);
      this.drawGuides(this.ctx);
      this.paintFlat();
      this.pushTexture();
      this.report();
      this.invalidateIfChanged();
    }

    /** Keep the hidden inputs in step with the design, both ways.
     *
     *  DOWN: any real change invalidates a previously applied design, so the order can
     *  never carry a recipe the customer has since edited. Compared by content rather than
     *  by "draw was called", because plenty of redraws (variant switch, image load,
     *  showing the stage) change nothing about the design.
     *
     *  UP: a valid design re-applies ITSELF. Apply used to be a gate, and pressing Add to
     *  Cart without pressing it first added the plain item and threw the design away —
     *  the customer's work was gone and they had to start over. That is the single most
     *  common way an order came in blank. The button now only collapses the editor.
     *
     *  ⚠️ `touched` is what keeps this honest. The panel SEEDS a placeholder — "Amy", or
     *  the monogram's AB — so there is something on the preview to react to, and nobody
     *  should be shipped a towel reading AMY because they opened the panel and closed it
     *  again. Auto-apply waits for the customer to touch a control; pressing Apply is
     *  still there for anyone who genuinely wants the placeholder. */
    invalidateIfChanged() {
      const was = this.applied;
      if (this.applied && this.designKey() !== this.appliedKey) {
        this.applied = false;
        this.clearProps();
      }
      if (!this.applied && this.touched && this.allEls().length
          && !this.allViolations().length) {
        this.writeProps();
        this.applied = true;
      }
      if (this.applied === was) return;
      this.saveUI();
      const t = this.q('open');
      if (t) {
        t.textContent = !this.q('body').hidden ? 'Hide personalization'
          : this.applied ? 'Edit personalization' : 'Add personalization';
      }
    }

    /** Content fingerprint of the design, for detecting edits after Apply. */
    designKey() {
      return JSON.stringify(this.sides.map((s) => (this.design[s] || []).map((e) => [
        e.type, e.value, e.slug, Math.round(e.x), Math.round(e.y),
        // e.colors is part of the design too: recolouring Sport's outlines after Apply
        // changes nothing else on this list, so without it the panel would stay "applied"
        // while the properties still described the previous colourway.
        e.size, e.rot || 0, e.color, e.font?.label, (e.colors || []).join(','),
      ])));
    }

    paintFlat() {
      if (this.mode === '3d' || !this.photoImg?.complete || !this.photoImg.naturalWidth) return;
      this.flat.width = this.photoImg.naturalWidth;
      this.flat.height = this.photoImg.naturalHeight;
      this.fctx.clearRect(0, 0, this.flat.width, this.flat.height);
      this.fctx.drawImage(this.photoImg, 0, 0);
      // Rect belongs to the CURRENT photo — a variant image has its own framing.
      const shot = this.photoFor(this.side);
      const [l, t, r, b] = this.photoRect || this.zone.photo.rect;
      this.placeArt(this.fctx, this.art, { ...shot, rect: [l, t, r, b] });
      // NO guides and NO scrim on the photo, ever — owner's call, 2026-08-09. This is the
      // product shot: it should read exactly as the photograph with the design on it, the
      // way it will look when it arrives. Dimming most of the garment to highlight a
      // rectangle made the preview about the RULES rather than about the product.
      // Nothing is lost: the artboard below is the drag surface and still draws both, so
      // the usable area is still visible where the customer is actually placing.
    }

    /** The variant Dawn currently has selected, from the markup it maintains. */
    currentVariant() {
      try {
        const el = document.querySelector('variant-selects [data-selected-variant]');
        return el ? JSON.parse(el.innerHTML) : null;
      } catch {
        return null;
      }
    }

    // ---------- 3D ----------
    async setMode(m) {
      const can3d = !!this.zone.model && hasWebGL();
      if (!can3d) {
        const tab = this.qa('[data-pz="view"][data-view="3d"]')[0];
        if (tab) tab.hidden = true;      // don't offer a view we cannot render
      }
      this.mode = m === '3d' && can3d ? '3d' : '2d';
      this.qa('[data-pz="view"]').forEach((b) => {
        b.setAttribute('aria-current', String(b.dataset.view === this.mode));
      });
      this.q('mvhost').hidden = this.mode !== '3d';
      this.flat.hidden = this.mode === '3d';
      if (this.mode !== '3d') return this.draw();

      try {
        await loadModelViewer();
      } catch {
        this.q('status').innerHTML = '<span class="pz__warn">3D preview unavailable.</span>';
        return this.setMode('2d');
      }
      if (!this.mv) {
        this.mv = document.createElement('model-viewer');
        this.mv.setAttribute('camera-controls', '');
        this.mv.setAttribute('touch-action', 'pan-y');
        this.mv.setAttribute('shadow-intensity', '0.85');
        this.mv.setAttribute('exposure', '1.1');
        this.mv.setAttribute('environment-image', 'neutral');
        this.mv.setAttribute('alt', this.zone.label || 'Product preview');
        this.q('mvhost').append(this.mv);
      }
      // .src is null (not '') before first assignment — String() guards the .includes.
      if (!String(this.mv.src || '').includes(this.zone.model)) {
        this.mv.src = this.zone.model;
        this.mv.addEventListener('load', () => { this.applyColorway(); this.draw(); }, { once: true });
      } else {
        this.draw();
      }
    }

    /** Recolour the model for the chosen variant.
     *
     *  A dedicated texture is exact but only exists for a couple of colourways. For every
     *  other colour, TINT the base material instead: baseColorFactor multiplies the
     *  material's own texture, so the weave/twill survives and only the hue changes. That
     *  is what makes a green pouch or a pink hat possible without authoring a texture per
     *  colour in Blender. Approximate where the model's base is strongly coloured. */
    async applyColorway() {
      const way = this.way;
      if (!way || !this.mv) return;
      await this.mv.updateComplete;
      const mat = this.mv.model?.materials?.find((m) => m.name === this.zone.base_material);
      if (!mat) return;
      const pbr = mat.pbrMetallicRoughness;
      if (way.tex) {
        pbr.setBaseColorFactor([1, 1, 1, 1]);
        pbr.baseColorTexture.setTexture(await this.mv.createTexture(way.tex));
        this.texKey = way.tex;
        return;
      }
      const rgb = hexToLinear(way.swatch);
      if (!rgb) return;
      // Reset to the NEUTRAL weave before tinting. baseColorFactor MULTIPLIES whatever
      // texture is currently on the material, and the model bakes a navy cover — so
      // without this the same colour renders differently depending on which colour was
      // selected before it: navy -> pink gave dark purple, blue -> pink gave lilac, and
      // beige -> pink happened to look right, which is what hid the bug.
      if (this.zone.neutral_tex && this.texKey !== this.zone.neutral_tex) {
        pbr.baseColorTexture.setTexture(await this.mv.createTexture(this.zone.neutral_tex));
        this.texKey = this.zone.neutral_tex;
      }
      pbr.setBaseColorFactor([...rgb, 1]);
    }

    pushTexture() {
      clearTimeout(this.pending);
      this.pending = setTimeout(async () => {
        try {
          if (this.mode !== '3d' || !this.mv) return;
          await this.mv.updateComplete;
          const slot = this.side === 'front' ? 'print-zone' : 'print-zone-back';
          const mat = this.mv.model?.materials?.find((m) => m.name === slot);
          if (!mat) return;
          const tex = await this.mv.createTexture(this.art.toDataURL('image/png'));
          mat.pbrMetallicRoughness.baseColorTexture.setTexture(tex);
        } catch (err) {
          console.warn('[personalizer] setTexture failed', err);
        }
      }, 130);
    }

    // ---------- elements ----------
    nextId() { return this.els.reduce((m, e) => Math.max(m, e.id), 0) + 1; }

    add(type) {
      if (this.els.length >= this.zone.max_elements) return;
      if (type === 'upload') { this.pickFile(); return; }
      // A monogram is a TEXT element pinned to a pair font, not a fourth element type.
      // Everything downstream — drawing, bounds, the rendered PNG, the order properties,
      // pricing — already handles text, and inventing a type would mean touching all of
      // it to gain nothing.
      const mono = type === 'monogram' ? this.pairFonts()[0] : null;
      if (type === 'monogram' && !mono) return;
      if (mono) type = 'text';
      // Resolved before the seed text, because how much of it fits depends on the face.
      const font = mono || this.defaultFont();
      const e = {
        id: this.nextId(), type,
        value: mono ? 'AB'
          : type === 'text' ? this.fitText('Amy', font, this.startSizeFor(font) / this.pxPerMm())
          : (this.icons[0]?.char ?? this.icons[0]?.name ?? ''),
        slug: type === 'icon' ? (this.icons[0]?.char ?? this.icons[0]?.slug) : undefined,
        x: this.CW / 2, y: this.CH / 2, size: this.defaultSize(), rot: this.startRot(),
        color: (this.colorsFor().find((c) => /black/i.test(c.name)) || this.colorsFor()[0]).hex,
        font, fontAuto: !mono,
      };
      // A stitched alphabet opens in its own colourway at one of its real sizes, so the
      // very first thing the customer sees is what the machine can actually sew.
      if (type === 'text') {
        this.seedBlockColors(e);
        // What the comment above has always promised. Then fitted, so a long name on a
        // small area still lands inside it rather than opening out of bounds.
        e.size = this.startSizeFor(font);
        this.shrinkToFit(e);
        this.clampToLimit(e);
      }
      this.els.push(e);
      this.sel = e.id;
      if (type === 'icon' && this.icons[0]) this.pickIcon(e, this.icons[0]);
      this.render();
    }

    /** Apply a catalogue entry to an element. A SYMBOL is a character — it has no image
     *  to fetch, takes the thread/vinyl colour like text, and any leftover `img` from a
     *  previously chosen stitch icon has to be cleared or it would keep drawing. */
    pickIcon(e, ic) {
      if (ic.symbol) {
        e.value = ic.char;
        e.slug = ic.char;
        e.img = null;
        this.draw();
        return;
      }
      this.loadIconImage(e, ic);
    }

    /** Can this icon be sewn here without dropping below safe stitch density?
     *
     *  An icon is ONE digitised file at ONE size — unlike an alphabet there is no tier to
     *  snap to, so the only question is whether 0.8x of it still fits. 48 of the 77 do not
     *  fit a 3in napkin and 47 do not fit the hat band; offering them lets a customer order
     *  something the machine cannot make, which surfaces after they have paid. */
    iconFits(ic) {
      if (!ic.w_mm || !ic.h_mm) return true;         // symbols: vinyl, scales freely
      const lim = this.elementLimitMm();
      // Scaled to the smallest the shop will make, does it still fit the product? Re-spacing
      // reaches any size, so this is about ROOM, not density — which is why the whole
      // library is on offer everywhere now rather than 29 of 77 on a napkin.
      // Shrunk until its LONGEST side reaches the floor, does it fit? Never scaled UP to
      // reach it — an icon already digitised smaller than the floor is fine as it is.
      const floor = this.zone.min_design_mm || 12.7;
      const k = Math.min(1, floor / Math.max(ic.w_mm, ic.h_mm));
      return ic.w_mm * k <= lim.w && ic.h_mm * k <= lim.h;
    }

    loadIconImage(e, icon) {
      const img = new Image();
      img.crossOrigin = 'anonymous';   // canvas stays untainted so toDataURL still works
      img.onload = () => this.draw();
      img.src = icon.url;
      e.img = img;
      e.slug = icon.slug;
      e.value = icon.name;
      // Open at the size it was DIGITISED at, not the 18mm text default. Nothing here
      // rescales an icon to a tier the way pick_tier does for lettering, so the size the
      // element happens to carry IS the scale factor: the median icon is 91mm tall, so the
      // shared default meant every icon started at 0.20x — five times outside the density
      // band, before the customer had touched anything.
      // Kept on the element so stitchScale() can tell how far it has been rescaled. The
      // <img> only knows pixels; the millimetres it was digitised at live in the catalogue.
      e.iconMm = icon.h_mm ? { w: icon.w_mm, h: icon.h_mm } : null;
      if (icon.h_mm) {
        e.size = Math.round(icon.h_mm * this.pxPerMm());
        this.clampToLimit(e);
      }
    }

    uploads() { return this.els.filter((e) => e.type === 'upload'); }

    pickFile() {
      const f = this.q('file');
      if (!f) return;
      f.value = '';
      f.click();
    }

    onFile(ev) {
      const f = ev.target.files[0];
      if (!f) return;
      const cfg = this.zone.uploads;
      // Validate at the boundary: a stranger chose this file. Fail with a reason.
      if (cfg.accept && !cfg.accept.includes(f.type)) {
        return this.failUpload(`${f.type || 'That file type'} is not accepted — PNG only, with a transparent background.`);
      }
      if (f.size > cfg.max_file_mb * 1024 * 1024) {
        return this.failUpload(`${(f.size / 1e6).toFixed(1)}MB is over the ${cfg.max_file_mb}MB limit.`);
      }
      if (this.uploads().length >= cfg.max) return this.failUpload(`${cfg.max} designs is the maximum.`);

      const r = new FileReader();
      r.onload = () => {
        const img = new Image();
        img.onload = () => {
          if (cfg.require_transparency && !hasAlpha(img)) {
            return this.failUpload(`${f.name} has no transparent background. The Cricut cuts the `
              + 'silhouette, so a solid backdrop would be cut out with the logo.');
          }
          this.els.push({
            // Keep the File itself, not just the decoded image. pickFile() clears the
            // input before every pick, so the <input> only ever holds the most recent
            // file — with five uploads allowed, four would never reach the order.
            id: this.nextId(), type: 'upload', value: f.name, img, file: f,
            x: this.CW / 2, y: this.CH / 2, size: this.defaultSize(), rot: this.startRot(),
            color: '#000', font: this.defaultFont(), fontAuto: true,
          });
          this.sel = this.els.at(-1).id;
          this.render();
        };
        img.onerror = () => this.failUpload('That file could not be read as an image.');
        img.src = r.result;               // data URL keeps the canvas untainted
      };
      r.onerror = () => this.failUpload('That file could not be read.');
      r.readAsDataURL(f);
    }

    failUpload(msg) {
      this.q('uploadnote').innerHTML = `<span class="pz__err">${esc(msg)}</span>`;
      const f = this.q('file');
      if (f) f.value = '';              // never submit a file we rejected
    }

    // ---------- UI ----------
    chips(host, items, label, isOn, on) {
      if (!host) return;
      host.innerHTML = '';
      items.forEach((it) => {
        const b = document.createElement('button');
        b.type = 'button';
        if (label === null) {
          b.className = 'pz__sw';
          b.style.background = it.hex || it.swatch || '#eee';
          b.title = it.name || '';
          if (it.tex) b.innerHTML = `<img src="${esc(it.tex)}" alt="${esc(it.name || '')}">`;
        } else {
          b.className = 'pz__chip';
          b.textContent = label(it);
        }
        b.onclick = () => {
          on(it);
          [...host.children].forEach((c, i) => c.setAttribute('aria-current', String(isOn(items[i]))));
        };
        host.append(b);
      });
      [...host.children].forEach((c, i) => c.setAttribute('aria-current', String(isOn(items[i]))));
      // Bring the CHOSEN one into view. The font row scrolls sideways now (see
      // personalizer.css), and the customer's face can be the eighth of ten — reopening
      // the editor would show a row scrolled to the start with no selection visible, so
      // it reads as nothing being picked. A no-op on the rows that still wrap.
      //
      // Only on render, never on click: the click handler re-flags aria-current itself
      // without coming back through here, so the row cannot jump under a finger
      // mid-tap. And scrollLeft rather than scrollIntoView, which walks up the ancestors
      // and would scroll the whole PAGE to a control the customer already had in view.
      const cur = host.querySelector('[aria-current="true"]');
      if (cur && host.scrollWidth > host.clientWidth) {
        const c = cur.getBoundingClientRect();
        const h = host.getBoundingClientRect();
        host.scrollLeft += (c.left - h.left) - (h.width - c.width) / 2;
      }
    }

    showSpec() {
      const z = this.zone;
      const d = z.designable;
      if (!this.admin) {
        this.q('spec').innerHTML = '<table>'
          + `<tr><td>Design area</td><td class="pz-v">${d.width} × ${d.height} mm</td></tr>`
          + `<tr><td>Up to</td><td class="pz-v">${z.max_elements} designs, ${z.max_chars} characters each</td></tr>`
          + '</table>';
        return;
      }
      this.q('spec').innerHTML = '<table>'
        + `<tr><td>Method</td><td class="pz-v">${esc(z.method)}</td></tr>`
        + `<tr><td>Product</td><td class="pz-v">${z.product.width} × ${z.product.height} mm</td></tr>`
        + `<tr><td>Designable</td><td class="pz-v">${d.width} × ${d.height} mm</td></tr>`
        + `<tr><td>Bounded by</td><td class="pz-v">${esc(z.bounded_by || '')}</td></tr>`
        + `<tr><td>Zone</td><td class="pz-v">${esc(z.zone)} @ ${esc(z.zone_version)}</td></tr>`
        + '</table>';
    }

    layers() {
      const host = this.q('layers');
      host.innerHTML = '';
      for (const e of this.els) {
        const d = document.createElement('div');
        d.className = 'pz__layer';
        d.setAttribute('aria-current', String(e.id === this.sel));
        const badge = e.type === 'icon' && e.img
          ? `<img class="pz__layer-badge" src="${esc(e.img.src)}" alt="">`
          : `<span class="pz__layer-badge" style="color:${esc(e.color)}">${e.type === 'upload' ? '▣' : 'T'}</span>`;
        const name = e.type === 'text' ? (e.value || '(empty)') : e.value;
        d.innerHTML = `${badge}<span class="pz__layer-name">${esc(name)}</span><span class="pz__layer-x">✕</span>`;
        d.onclick = (ev) => {
          if (ev.target.classList.contains('pz__layer-x')) {
            if (e.type === 'upload') { const f = this.q('file'); if (f) f.value = ''; }
            this.els = this.els.filter((o) => o !== e);
            if (this.sel === e.id) this.sel = null;
          } else this.sel = e.id;
          this.render();
        };
        host.append(d);
      }
    }

    editor() {
      const host = this.q('editor');
      const e = this.els.find((o) => o.id === this.sel);
      if (!e) {
        host.innerHTML = `<label>${this.els.length ? 'Select a design to edit it' : 'Add text or an icon to begin'}</label>`;
        return;
      }
      // Size and rotation are placement controls. Where the zone owns both — an autofit
      // strip with no preview to place anything against — showing them offers the customer
      // only ways to make it wrong, and a disabled slider still reads as a broken one.
      // The zone owns size and rotation whenever it auto-fits a single line — whether or
      // not there is a preview. Showing a slider that cannot change anything reads as a
      // broken control, and a rotation slider on a spine only offers ways to get it wrong.
      const fixed = this.noArtboard && e.type === 'text';
      // The slider spans what this design can actually be MADE at — see sizeRangePx. When
      // that leaves a single size, there is no choice to offer and the control says so
      // rather than presenting a slider that cannot move.
      const range = this.sizeRangePx(e);
      const sizeRot = fixed ? '' : `
        <label>Size <span data-pz="sizeval" style="float:right">${this.capMm(e)}mm</span></label>
        ${range.only
          ? `<div class="pz__note">Only one size fits here.</div>`
          : `<input type="range" data-pz="size" min="${range.min}" max="${range.max}"
               value="${Math.min(Math.max(e.size, range.min), range.max)}"
               ${this.zone.autofit && e.type === 'text' ? 'disabled' : ''}>`}
        <label>Rotation <span data-pz="rotval" style="float:right">${e.rot || 0}°</span></label>
        <input type="range" data-pz="rot" min="-180" max="180" value="${e.rot || 0}">`;

      if (e.type === 'text') {
        // One picker per thread block. Sport gets Fill / Inner outline / Outer outline;
        // a single-colour alphabet keeps the one row it always had.
        const blocks = this.activeBlocks(e);
        const colourCtl = blocks
          ? blocks.map((b, i) => `<label>${esc(b.label)}</label>`
            + `<div class="pz__row" data-pz="threads${i}"></div>`).join('')
          : '<label>Colour</label><div class="pz__row" data-pz="threads"></div>';
        // A monogram is chosen, not typed. Two pickers say "this is one design from two
        // initials"; a text box says "write a name here" and then quietly eats all but
        // the first two letters.
        const pair = !!e.font?.pairs;
        const AZ = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));
        const pick = (slot, cur) => `<select data-pz="${slot}">`
          + AZ.map((c) => `<option value="${c}"${c === cur ? ' selected' : ''}>${c}</option>`).join('')
          + '</select>';
        const val = (e.value || 'AB').toUpperCase();
        // ⚠️ ONLY the value control differs. Branching on the whole template dropped the
        // font row, the colour rows and the size slider from every monogram, and q('fonts')
        // then threw — the panel rendered, so it looked fine until it was clicked.
        const valueCtl = pair
          ? `<label>Initials</label>
             <div class="pz__row">${pick('i1', val[0] || 'A')}${pick('i2', val[1] || 'B')}</div>`
          // No maxlength. The real limit is how wide the TEXT comes out, not how many
          // characters it has — see fitText — so the input takes what they type and trims
          // to what can be stitched. A fixed attribute here refused LILI on a napkin that
          // WWW fits.
          : `<label>Text <span data-pz="count" style="float:right">${e.value.length}/${this.roomFor(e)}</span></label>
             <input type="text" data-pz="val" value="${esc(e.value)}">`;
        host.innerHTML = `
          ${valueCtl}
          <label>Font</label><div class="pz__row" data-pz="fonts"></div>
          ${colourCtl}${sizeRot}`;
        if (pair) {
          const setInitial = () => {
            e.value = (this.q('i1').value || 'A') + (this.q('i2').value || 'B');
            this.draw();
            this.layers();
            // The GLYPH changes with the initials, and with it how many thread blocks
            // exist — 10 of the 351 pairs are digitised as one. Rebuild so the colour
            // rows describe the pair actually chosen rather than the previous one.
            if ((this.activeBlocks(e) || []).length
                !== this.querySelectorAll('[data-pz^="threads"]').length) this.editor();
          };
          this.q('i1').onchange = setInitial;
          this.q('i2').onchange = setInitial;
        } else {
          this.q('val').oninput = (ev) => {
            const typed = e.font?.glyphs
              ? PersonalizerPanel.stitchable(ev.target.value, e.font)
              : ev.target.value;
            e.value = this.fitText(typed, e.font);
            if (e.value !== ev.target.value) {
              const at = ev.target.selectionStart;
              ev.target.value = e.value;
              // Trimming resets the caret to the end, which fights anyone editing the
              // middle of a name. Put it back where it was, clamped to the new length.
              const p = Math.min(at ?? e.value.length, e.value.length);
              ev.target.setSelectionRange(p, p);
            }
            const counter = this.q('count');
            if (counter) counter.textContent = `${e.value.length}/${this.roomFor(e)}`;
            this.draw();
            this.layers();
          };
        }
        this.chips(this.q('fonts'), this.fontsForElement(e), (f) => f.label,
          (f) => f.key ? f.key === e.font.key : f === e.font,
          (f) => {
            e.font = f;
            e.fontAuto = false;      // chosen, so the catalogue must not overwrite it
            // A stitched alphabet cannot set what it has no glyph for. Reduce the text
            // rather than let the customer type something the preview quietly drops.
            if (f.glyphs) e.value = PersonalizerPanel.stitchable(e.value, f);
            // Same reason, for LENGTH: Kendall is 1.7x wider than Navidad at its smallest
            // safe size, so a name that fit one face has to be trimmed for the other.
            // Without this the input re-renders with a shorter maxlength and the overflow
            // stays in e.value — invisible in the field, still stitched.
            e.value = e.value.slice(0, this.maxCharsFor(f));
            // The new face owns its own colourway and its own real sizes. Carrying the
            // old font's three Sport threads onto a single-colour alphabet, or its size
            // onto an alphabet that has no such tier, both end as something unstitchable.
            this.seedBlockColors(e);
            // ...and its own real SIZES, which is the other half of that sentence and was
            // never implemented. Kendall's smallest file is 63.8mm against Navidad's
            // 25.9mm, so carrying a size across faces lands 2.5x off what the new one can
            // be run at.
            e.size = this.startSizeFor(f);
            this.shrinkToFit(e);
            this.clampToLimit(e);
            this.editor();
            this.draw();
          });
        // What this FACE can and cannot do, said before the customer commits to it.
        // Three cases, and the third is new: a webfont on an embroidery product has no
        // stitch file at all, so the lettering is digitised by hand for that order. That
        // is a lead-time fact the customer is entitled to before paying, not after.
        const faceNote = e.font?.glyphs
          // A pair font is not an alphabet and the A-Z wording would be a lie: you cannot
          // set a name in it at all, only two interlocked initials.
          ? (e.font.pairs
            ? 'Interlocking monogram — two initials, woven into one design. '
              + 'Every combination A–Z is available.'
            : 'Stitched alphabet — capital letters A–Z and spaces only. '
              + 'Need accents, numbers or punctuation? Order it and note what you want, '
              + 'and we will digitise the lettering by hand.')
          : (this.zone.method === 'embroidery'
            ? 'Hand-lettered — we digitise this face to a stitch file for your order, so '
              + 'it can take a little longer than our stitched alphabets. Accents, numbers '
              + 'and punctuation are all fine.'
            : null);
        if (faceNote) {
          const note = document.createElement('p');
          note.className = 'pz__note';
          note.textContent = faceNote;
          this.q('fonts').after(note);
        }
      } else if (e.type === 'upload') {
        host.innerHTML = `<label>Your artwork</label><div style="font-size:13px">${esc(e.value)}</div>${sizeRot}`;
      } else {
        host.innerHTML = `<label>Icon</label><div class="pz__iconpick" data-pz="icons"></div>${sizeRot}`;
        this.renderIconPicker(e);
      }
      const blocks = this.activeBlocks(e);
      if (blocks) {
        blocks.forEach((b, i) => this.chips(this.q(`threads${i}`), this.colorsFor(), null,
          (t) => t.hex === this.blockColors(e)[i],
          (t) => {
            e.colors = this.blockColors(e);
            e.colors[i] = t.hex;
            if (!i) e.color = t.hex;   // block 0 IS e.color, so the layer badge follows
            this.draw();
            this.layers();
          }));
      } else if (e.type !== 'upload') {
        this.chips(this.q('threads'), this.colorsFor(), null, (t) => t.hex === e.color,
          (t) => { e.color = t.hex; this.draw(); this.layers(); });
      }
      // Optional: a zone that owns sizing and rotation renders neither slider.
      const sizeEl = this.q('size');
      if (sizeEl) sizeEl.oninput = (ev) => {
        // Snapped, because the safe sizes are bands with gaps between tiers — the slider's
        // own min/max cannot express that.
        e.size = +ev.target.value;
        const out = this.q('sizeval');
        if (out) out.textContent = `${this.capMm(e)}mm`;
        this.draw();
      };
      const rotEl = this.q('rot');
      if (rotEl) rotEl.oninput = (ev) => {
        e.rot = +ev.target.value;
        this.q('rotval').textContent = `${e.rot}°`;   // NOT editor() — that kills this input
        this.draw();
      };
    }

    renderIconPicker(e) {
      const host = this.q('icons');
      if (!host) return;
      if (!this.icons.length) { host.innerHTML = '<label>Loading icons…</label>'; return; }
      host.innerHTML = '';
      for (const ic of this.icons) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'pz__icon';
        const key = ic.symbol ? ic.char : ic.slug;
        b.title = ic.symbol ? ic.name : `${ic.name} · ${ic.w_mm}×${ic.h_mm}mm`;
        b.setAttribute('aria-current', String(key === e.slug));
        b.innerHTML = ic.symbol
          ? `<span class="pz__sym" aria-label="${esc(ic.name)}">${esc(ic.char)}</span>`
          : `<img loading="lazy" src="${esc(ic.url)}" alt="${esc(ic.name)}">`;
        b.onclick = () => {
          this.pickIcon(e, ic);
          [...host.children].forEach((c, i) => c.setAttribute(
            'aria-current', String((this.icons[i].symbol ? this.icons[i].char : this.icons[i].slug) === key)));
          this.layers();
        };
        host.append(b);
      }
    }

    /** The "+ Monogram" button only exists if a pair font is in the catalogue, which
     *  arrives after the panel mounts — so the button ships hidden and is revealed here
     *  rather than promising a design the catalogue may not carry. */
    monogramUI() {
      const btn = this.querySelector('[data-pz="add"][data-type="monogram"]');
      if (btn) btn.hidden = !this.pairFonts().length;
    }

    uploadUI() {
      const cfg = this.zone.uploads;
      const btn = this.querySelector('[data-pz="add"][data-type="upload"]');
      const note = this.q('uploadnote');
      if (!cfg?.enabled) {
        // Uploads are off for more than one reason. Embroidery cannot be priced without
        // hand-digitising; a 13mm spine simply has nowhere to put artwork. The zone says
        // which, and only falls back to the embroidery wording when it does not — telling
        // a vinyl hardcover buyer about stitch files is just noise.
        if (note) {
          note.textContent = cfg?.note
            || 'Your own artwork can only be added to vinyl items. Embroidery designs have '
             + 'to be digitised by hand, so we cannot price them here.';
        }
        return;
      }
      if (btn) btn.disabled = this.uploads().length >= cfg.max;
      if (note) {
        note.innerHTML = `${this.els.length}/${this.zone.max_elements} designs · `
          + `${this.uploads().length}/${cfg.max} uploads<br>`
          + `PNG with a transparent background, up to ${cfg.max_file_mb}MB`;
      }
    }

    /** What this design costs, in cart lines. One place, so the panel's estimate and the
     *  actual charge can never disagree — they used to be the same arithmetic written
     *  twice, which is how a display-only price survives into production unnoticed. */
    /** Every element on the ITEM, across all sides. `els` is per-side, which is right for
     *  editing and wrong for charging: a name on the front and a monogram on the back is
     *  two designs, but each side saw its own "first one included" allowance and both came
     *  out free. One item, one allowance. */
    allEls() { return this.sides.flatMap((s) => this.design[s] || []); }

    /** Is there a price list for this zone at all? Separate from `uploads.enabled`.
     *
     *  ⚠️ These were ONE flag and it silently made every embroidery product free.
     *  `enabled` means "the customer may upload artwork", which is vinyl-only because
     *  embroidery artwork has to be digitised by hand — and it was ALSO gating the whole
     *  price list, so sweaters, napkins, pouches, towels and hats charged nothing for a
     *  design. Invisible on the live store only because Zepto still serves those products
     *  and takes its own $10; it would have become a revenue hole the day Redesign v1
     *  published. Uploads and money are unrelated questions and are now asked separately. */
    priced() {
      if (this.zone.pricing) return this.zone.pricing;
      // Shim for a product whose metafield has not been rewritten yet. Same numbers, old
      // shape. Delete once every product carries `pricing`.
      const u = this.zone.uploads;
      const a = this.zone.addons;
      if (!u || u.extra_price == null || !a) return null;
      return { included: u.included,
               extra: { id: a.extra.id, price: u.extra_price, label: 'extra design' },
               upload: { id: a.upload.id, price: u.upload_price, label: 'upload' } };
    }

    /** THE price list — one method producing both the estimate and the actual cart lines.
     *
     *  They used to be the same arithmetic written twice, which is exactly how the panel
     *  once displayed a price and charged nothing. Anything shown to the customer and
     *  anything posted to /cart/add.js comes from this array. */
    charges() {
      const cfg = this.priced();
      const out = { lines: [], total: 0, free: 0, extra: 0, uploads: 0 };
      if (!cfg) return out;
      const all = this.allEls();
      // Uploads can only be charged where they can be MADE. Counting them on a zone that
      // does not accept them would invoice for something the customer could not have done.
      const ups = this.zone.uploads?.enabled ? all.filter((e) => e.type === 'upload') : [];
      const rest = all.filter((e) => e.type !== 'upload');

      // A face with its own price is never DISCOUNTED, but it still OCCUPIES the free
      // allowance — it is a design like any other, it just costs what it costs. Sport is
      // $20 for the first and $15 for each after, because three thread blocks mean two
      // extra thread changes and far more machine time.
      // ⚠️ Letting a priced face sit outside the allowance instead of consuming it handed
      // the customer a free second design: Sport + one ordinary name came to $20, with the
      // ordinary name free, when it should be $35.
      const faces = cfg.fonts || {};
      const byFace = new Map();
      const plain = [];
      for (const e of rest) {
        const k = e.type === 'text' && e.font?.key;
        if (k && faces[k]) byFace.set(k, (byFace.get(k) || 0) + 1);
        else plain.push(e);
      }
      const add = (spec, qty) => {
        if (!spec?.id || qty <= 0) return;
        out.lines.push({ id: spec.id, price: spec.price || 0, qty, label: spec.label });
        out.total += (spec.price || 0) * qty;
      };
      let priced = 0;
      for (const [k, n] of byFace) {
        add(faces[k].first, 1);
        add(faces[k].extra, n - 1);
        priced += n;
      }
      // Whatever the priced faces did not use up is what is left to give away.
      const freeLeft = Math.max(0, (cfg.included ?? 1) - priced);
      out.free = Math.min(plain.length, freeLeft);
      out.extra = Math.max(0, plain.length - freeLeft);
      add(cfg.extra, out.extra);
      out.uploads = ups.length;
      add(cfg.upload, out.uploads);
      return out;
    }

    priceUI() {
      const cfg = this.priced();
      const host = this.q('price');
      if (!cfg) { host.innerHTML = ''; return; }
      // Item-level, matching charges(). Reading this.els here would show "First design
      // free" per FACE and disagree with what is actually charged.
      const c = this.charges();
      // Every row comes from the SAME array the cart is built from, so the estimate cannot
      // describe one thing while the customer is charged for another.
      const rows = c.free
        ? [`<tr><td>First design${c.free > 1 ? `s (${c.free})` : ''}</td>`
           + '<td class="pz-v">free</td></tr>']
        : [];
      for (const l of c.lines) {
        const label = `${l.qty} ${l.label}${l.qty > 1 ? 's' : ''} × $${l.price.toFixed(2)}`;
        rows.push(`<tr><td>${esc(label)}</td>`
          + `<td class="pz-v">$${(l.price * l.qty).toFixed(2)}</td></tr>`);
      }
      const total = c.total;
      host.innerHTML = `<table>${rows.join('')}`
        + '<tr><td><strong>Personalization</strong></td><td class="pz-v"><strong>'
        + `${total ? `+$${total.toFixed(2)}` : 'included'}</strong></td></tr></table>`;
    }

    report() {
      const bad = this.violations();
      const host = this.q('status');
      if (!this.admin) {
        host.innerHTML = bad.length
          ? [...new Set(bad.map((b) => (b.why.startsWith('outside')
            ? 'Move this design inside the printable area.'
            : 'Move these designs further apart.')))]
            .map((m) => `<span class="pz__warn">${m}</span>`).join('<br>')
          : this.els.length ? 'Looks good.' : 'Add text, an icon or your own artwork to begin.';
        return;
      }
      const lines = this.els.map((e) => {
        const m = this.mmOf(e);
        return `${e.type}-${e.id} ${e.type === 'text' ? `“${esc(e.value)}” ` : `${esc(e.value)} `}`
          + `x ${m.x} y ${m.y} mm · ${e.size}px${e.rot ? ` · ${e.rot}°` : ''}`;
      });
      host.innerHTML = `<span class="pz__admin">${lines.join('<br>') || 'Nothing placed yet.'}</span>`
        + (bad.length ? `<br><span class="pz__warn">${bad.length} problem(s):</span> `
          + bad.map((b) => `${b.e.type}-${b.e.id} ${b.why}`).join('; ') : '');
    }

    sidesUI() {
      const host = this.q('sides');
      if (!host) return;
      this.chips(host, this.sides, (s) => s[0].toUpperCase() + s.slice(1), (s) => s === this.side, (s) => {
        this.side = s;
        this.sel = null;
        this.render();
      });
    }

    // ---------- line item properties ----------
    clearProps() {
      this.querySelectorAll('[data-pz-slot], [data-pz-field="design"]').forEach((i) => {
        i.disabled = true;
        i.value = '';
      });
      const zv = this.querySelector('[data-pz-field="zone_version"]');
      if (zv) zv.disabled = true;
    }

    /** Write the design into the hidden inputs. Readable properties for the packing slip,
     *  plus `_design` as the exact recipe for scripts/order-preview.sh. */
    writeProps() {
      this.clearProps();
      const recipe = {};
      for (const side of this.sides) {
        const list = this.design[side];
        if (!list.length) continue;
        recipe[side] = [];
        list.forEach((e, idx) => {
          const n = idx + 1;
          const m = this.mmOf(e);
          // Carry the CODE where the palette has one: "900 Black" is what gets pulled
          // off the thread rack, and a bare "Black" makes whoever stitches it guess.
          const named = (hex) => {
            const s = this.colorsFor().find((t) => t.hex === hex);
            return s ? (s.code ? `${s.code} ${s.name}` : s.name) : hex;
          };
          const swatch = this.colorsFor().find((t) => t.hex === e.color);
          const thread = swatch
            ? { ...swatch, name: swatch.code ? `${swatch.code} ${swatch.name}` : swatch.name }
            : undefined;
          // A multi-block alphabet needs every spool named, and there is ONE `Colour N`
          // slot per element. Joining them keeps snippets/personalizer.liquid untouched
          // and still reads correctly on the packing slip.
          const blocks = this.activeBlocks(e);
          const cols = this.blockColors(e);
          const set = (field, value) => {
            const i = this.querySelector(`[data-pz-slot="${side}-${n}"][data-pz-field="${field}"]`);
            if (!i || value == null || value === '') return;
            i.value = value;
            i.disabled = false;
          };
          set('label', e.type === 'text' ? `“${e.value}”`
            : e.type === 'icon' ? e.value : `Your artwork (${e.value})`);
          if (e.type === 'text') set('font', e.font.label);
          if (e.type !== 'upload') {
            set('colour', blocks
              ? blocks.map((b, i) => `${b.label} ${named(cols[i])}`).join(' · ')
              : thread?.name || e.color);
          }
          set('position', `${m.x}mm, ${m.y}mm${e.rot ? `, ${e.rot}°` : ''}`);
          recipe[side].push({
            type: e.type, value: e.value, slug: e.slug,
            x: m.x, y: m.y, size: e.size, rotation: e.rot || 0,
            color: e.color, font: e.type === 'text' ? e.font.label : undefined,
            // What scripts/order-preview.py needs to emit the .PES: which alphabet, which
            // digitised size, and a thread per block. The LABEL is for humans and is not
            // stable enough to look a font up by.
            font_key: e.type === 'text' ? e.font.key : undefined,
            size_mm: this.capMm(e),
            colors: blocks ? cols : undefined,
          });
        });
      }
      const d = this.querySelector('[data-pz-field="design"]');
      d.value = JSON.stringify({ zone: this.zone.zone, sides: recipe });
      d.disabled = false;
      this.appliedKey = this.designKey();
      const zv = this.querySelector('[data-pz-field="zone_version"]');
      if (zv) zv.disabled = false;
    }

    saveUI() {
      const bad = this.allViolations();
      const b = this.q('save');
      const note = this.q('savenote');
      const empty = this.sides.every((s) => !this.design[s].length);
      b.disabled = bad.length > 0 || empty;
      if (this.applied) {
        note.innerHTML = '<span class="pz__ok">Personalization applied — it will be added with this item.</span>';
      } else if (bad.length) {
        note.innerHTML = this.admin
          ? `<span class="pz__err">Cannot apply:</span> ${bad.map((v) => `${v.side}/${v.e.type}-${v.e.id} ${v.why}`).join('; ')}`
          : '<span class="pz__err">Fix the highlighted designs first.</span>';
      } else {
        // Not applied, nothing wrong with it: this is the seeded placeholder nobody has
        // touched yet. Say what makes it theirs, not "press Apply" — Apply is no longer
        // what attaches it.
        note.textContent = empty ? ''
          : 'Edit this design and it is added to your item automatically.';
      }
    }

    render() {
      this.applyFit();       // before anything reads e.size
      this.setMode(this.mode);
      this.sidesUI();
      this.layers();
      this.editor();
      this.uploadUI();
      this.monogramUI();
      // Only advertise the photo as draggable when flatAt() would actually accept a drag.
      this.flat?.classList.toggle('is-editable', !this.q('body')?.hidden
        && !(this.photoFor(this.side)?.quad?.length === 4));
      this.priceUI();
      this.draw();
      this.saveUI();
    }

    // ---------- wiring ----------
    wire() {
      const body = this.q('body');
      const toggle = this.q('open');
      toggle.onclick = () => {
        const open = body.hidden;
        body.hidden = !open;
        toggle.setAttribute('aria-expanded', String(open));
        toggle.textContent = open ? 'Hide personalization' : 'Add personalization';
        if (open) this.firstOpen();
        // Closing with a design applied keeps the personalized shot up — that IS the
        // product they are buying. Closing with nothing applied restores the gallery.
        this.showStage(open || this.applied);
        if (!open) this.editing = false;
      };

      // What separates "the customer designed something" from "the panel seeded a
      // placeholder". Auto-apply hangs off it — see invalidateIfChanged.
      //
      // One delegated listener per surface rather than a line in each of the dozen
      // handlers (text, initials, font chips, thread chips, size, rotation, icon picker,
      // layer delete, drag) — every one of those is a control INSIDE .pz__controls or a
      // drag on the product photo. Deliberately NOT the open/close toggle: opening the
      // panel is what a customer does BEFORE deciding anything.
      const touch = () => { this.touched = true; };
      const controls = this.querySelector('.pz__controls');
      controls?.addEventListener('pointerdown', touch, true);
      controls?.addEventListener('input', touch, true);
      controls?.addEventListener('change', touch, true);
      this.flat?.addEventListener('pointerdown', touch, true);

      this.qa('[data-pz="view"]').forEach((b) => {
        b.onclick = () => this.setMode(b.dataset.view);
      });
      this.querySelectorAll('[data-pz="add"]').forEach((b) => {
        b.onclick = () => this.add(b.dataset.type);
      });
      const f = this.q('file');
      if (f) f.onchange = (ev) => this.onFile(ev);

      this.q('save').onclick = () => {
        if (this.allViolations().length) return;
        this.writeProps();
        this.applied = true;
        this.saveUI();
        // Collapse back to the product view: the preview stays up, now without guides,
        // so what they see is the product with their personalization on it.
        body.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
        toggle.textContent = 'Edit personalization';
        this.showStage(true);
        this.draw();
        this.stageEl?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      };

      // ---------- drag ----------
      // BOTH surfaces move the design: the artboard, and the product photo itself.
      // Judging placement on the photo while dragging on a separate grid is the kind of
      // indirection that ends in a design landing somewhere the customer did not intend.
      this.dragOn(this.artboard, (ev) => {
        const r = this.artboard.getBoundingClientRect();
        return {
          x: (ev.clientX - r.left) / r.width * this.CW,
          y: (ev.clientY - r.top) / r.height * this.CH,
        };
      });
      if (this.flat) this.dragOn(this.flat, (ev) => this.flatAt(ev));
    }

    /** One drag state machine, driven by whichever surface reports the pointer.
     *  `map` returns ARTBOARD coordinates, or null when this surface cannot place
     *  anything right now. */
    dragOn(el, map) {
      el.addEventListener('pointerdown', (ev) => {
        const p = map(ev);
        if (!p) return;
        const hit = [...this.els].reverse().find((e) => {
          const b = this.bbox(e);
          return Math.abs(p.x - e.x) < b.w / 2 + 10 && Math.abs(p.y - e.y) < b.h / 2 + 10;
        });
        if (!hit) return;
        this.sel = hit.id;
        this.drag = { e: hit, dx: p.x - hit.x, dy: p.y - hit.y };
        // Capture is an optimisation — it keeps the drag alive if the pointer leaves the
        // canvas. It throws when the pointer is already gone, and an uncaught throw here
        // would skip the render below and leave the panel mid-drag.
        try { el.setPointerCapture(ev.pointerId); } catch { /* pointer already released */ }
        el.classList.add('is-dragging');
        this.render();
      });
      el.addEventListener('pointermove', (ev) => {
        if (!this.drag) return;
        const p = map(ev);
        if (!p) return;
        // Never blocked. Only the centre is clamped, so nothing can be lost off-canvas;
        // violations are reported and gate Save.
        this.drag.e.x = Math.max(0, Math.min(this.CW, p.x - this.drag.dx));
        this.drag.e.y = Math.max(0, Math.min(this.CH, p.y - this.drag.dy));
        this.draw();
      });
      const stop = () => { this.drag = null; el.classList.remove('is-dragging'); };
      el.addEventListener('pointerup', stop);
      el.addEventListener('pointercancel', stop);
    }

    /** Pointer -> ARTBOARD coordinates from a click on the PRODUCT PHOTO.
     *
     *  Three transforms have to be undone, and getting any one of them wrong misplaces the
     *  design without anything throwing:
     *    1. the canvas is BORDERED, so getBoundingClientRect is the BORDER box while the
     *       bitmap fills the CONTENT box;
     *    2. it is `object-fit: contain` inside a SQUARE element, so a portrait photo is
     *       letterboxed and does not fill the content box either — the same "the element's
     *       real box is not the box the code assumed" trap that killed dragging twice
     *       before;
     *    3. placeArt drew the artboard into the calibrated rect, optionally rotated about
     *       that rect's centre.
     *
     *  Returns null rather than guessing when the surface cannot be inverted:
     *    - a QUAD (the slanted hardcover spine) is not an affine rect map. Those zones are
     *      preview:"none", so there is nothing on screen to drag anyway.
     *    - the panel is COLLAPSED. After Apply the photo is the product shot, not an
     *      editor; dragging there would silently un-apply the design, and with the panel
     *      shut the customer would never see the Save button come back — they would add to
     *      cart with no personalization at all. */
    flatAt(ev) {
      if (this.q('body')?.hidden) return null;
      const shot = this.photoFor(this.side);
      if (shot?.quad?.length === 4) return null;
      const cv = this.flat;
      if (!cv?.width || !cv.height) return null;
      const box = cv.getBoundingClientRect();
      const bw = parseFloat(getComputedStyle(cv).borderLeftWidth) || 0;
      const boxW = box.width - bw * 2;
      const boxH = box.height - bw * 2;
      if (boxW <= 0 || boxH <= 0) return null;
      const s = Math.min(boxW / cv.width, boxH / cv.height);          // object-fit: contain
      const px = (ev.clientX - box.left - bw - (boxW - cv.width * s) / 2) / s;
      const py = (ev.clientY - box.top - bw - (boxH - cv.height * s) / 2) / s;

      const [l, t, r, b] = this.photoRect || this.zone.photo.rect;
      const rot = +shot?.rotate || 0;
      if (!rot) {
        return { x: (px - l) / (r - l) * this.CW, y: (py - t) / (b - t) * this.CH };
      }
      // Undo the rotation about the rect's centre, then the quarter-turn side swap.
      const a = (-rot * Math.PI) / 180;
      const dx = px - (l + r) / 2;
      const dy = py - (t + b) / 2;
      const rx = dx * Math.cos(a) - dy * Math.sin(a);
      const ry = dx * Math.sin(a) + dy * Math.cos(a);
      const quarter = Math.abs(rot % 180) === 90;
      const w = quarter ? b - t : r - l;
      const h = quarter ? r - l : b - t;
      return { x: (rx / w + 0.5) * this.CW, y: (ry / h + 0.5) * this.CH };
    }

    /** Move the preview into the product's media column so it REPLACES the product photo
     *  instead of sitting in a second, smaller viewport beside it. Done from JS rather
     *  than Liquid because the gallery is rendered by a different snippet. */
    relocateStage() {
      const wrapper = document.querySelector('.product__media-wrapper');
      const stage = this.stageEl;
      if (!wrapper || !stage || this.stageMoved) return;
      this.gallery = wrapper.querySelector('media-gallery');
      wrapper.prepend(stage);
      stage.classList.add('pz__stage--in-gallery');

      // A way back to the real product photos; the preview replaces them, it must not
      // trap the customer away from them.
      const back = document.createElement('button');
      back.type = 'button';
      back.className = 'pz__backbtn';
      back.textContent = 'Product photos';
      back.onclick = () => this.showStage(false);
      stage.querySelector('[data-pz="viewtabs"]').append(back);

      const note = document.createElement('p');
      note.className = 'pz__stagenote';
      note.dataset.pz = 'stagenote';
      stage.append(note);

      this.stageMoved = true;
      this.showStage(true);
    }

    /** Preview visible => gallery hidden. Both stay in the DOM so nothing has to be
     *  re-rendered when the customer flips between them. */
    showStage(on) {
      if (!this.stageMoved) return;
      const stage = this.stageEl;
      stage.hidden = !on;
      if (this.gallery) this.gallery.hidden = on;
      const note = this.q('stagenote');
      if (note) {
        note.textContent = this.applied
          ? 'Your personalization — this is how it will be made.'
          : this.noArtboard
            // No artboard to point at: on a spine the size and placement are fixed, so
            // the only thing the customer changes is the words.
            ? 'Live preview. Type your text to see it on the spine.'
            : 'Live preview. Drag your design on the artboard below.';
      }
      if (on) this.draw();
    }

    /** Colour comes from the product's own variant picker, not a second set of swatches
     *  inside the panel — two controls for one decision is how you get them disagreeing.
     *  Matched on the variant's option value against colorways[].option / .name. */
    colorwayFor(variant) {
      const ways = this.zone.colorways || [];
      if (!ways.length) return null;
      if (!variant) return ways[0];
      const values = (variant.options || []).map((v) => String(v).trim().toLowerCase());
      const title = String(variant.title || '').toLowerCase();
      return ways.find((w) => {
        const keys = [w.option, w.name].filter(Boolean).map((k) => String(k).trim().toLowerCase());
        return keys.some((k) => values.includes(k) || title.split(' / ').includes(k));
      }) || null;
    }

    /** Apply a variant: its own photo for 2D (when the colourway carries a calibrated
     *  rect for it) and its colourway texture for 3D. */
    applyVariant(variant) {
      this.variant = variant || this.variant;
      const way = this.colorwayFor(this.variant);
      if (way) this.way = way;

      // 2D. A photo is only usable if we know where the design area sits IN THAT PHOTO;
      // a variant image with no calibrated rect would put the design in the wrong place.
      const shot = (this.way && this.way.photo) || this.zone.photo;
      if (shot && shot.image !== this.photoSrc) {
        this.photoSrc = shot.image;
        this.photoRect = shot.rect || this.zone.photo.rect;
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => this.draw();
        img.src = shot.image;
        this.photoImg = img;
      }
      this.applyColorway();     // 3D
      this.draw();
    }

    /** Everything expensive waits for the panel to actually be opened. */
    /** Some products have nothing honest to preview against.
     *
     *  The hardcover spine is the case: every spine photograph in the catalogue is of an
     *  ALREADY-PERSONALIZED book, so compositing a customer's name onto one shows it on
     *  top of somebody else's. And the design area is a 13x186mm strip, which renders as a
     *  700px-tall column that nobody can drag along. A text field is not a downgrade here
     *  — it is the only version that does not lie. Set `preview: "none"` on the zone.
     *
     *  Production still gets everything: the recipe, and the rendered artwork PNG, which
     *  is drawn on an offscreen canvas and never needed the on-screen stage. */
    get noPreview() { return this.zone.preview === 'none'; }

    /** Is there anything to drag? On a spine the size is auto-fitted and only one line is
     *  allowed, so the artboard offers no decision — and being a 13x186mm strip it renders
     *  as a 700px-tall column that dwarfs the page for no purpose. */
    get noArtboard() {
      return !!this.zone.autofit && (this.zone.max_elements || 1) <= 1;
    }

    /** The element the panel opens with.
     *
     *  Napkins and guest towels open on the interlocking MONOGRAM (owner, 2026-08-11) —
     *  it is what most of them are ordered with, and starting on a text box made the
     *  monogram look like an afterthought behind a button.
     *
     *  ⚠️ A monogram needs a pair font, and the font catalogue is FETCHED — it does not
     *  exist when the panel first opens. So a monogram zone seeds from that fetch instead,
     *  and falls back to text if the catalogue never arrives. Guarded by els.length rather
     *  than a flag because both callers can reach it. */
    seedDefault() {
      if (this.els.length) return;
      const want = this.zone.default_element || 'text';
      this.add(want === 'monogram' && !this.pairFonts().length ? 'text' : want);
    }

    firstOpen() {
      if (this.opened) return;
      this.opened = true;

      // Only the PREVIEW is skipped. Seeding the first element, following the variant and
      // the whole save path still run — an early return here silently left the panel with
      // no text element at all.
      if (this.noArtboard) {
        const wrap = this.querySelector('.pz__artboardwrap');
        if (wrap) wrap.hidden = true;
      }
      if (this.noPreview) {
        if (this.stageEl) this.stageEl.hidden = true;
      } else {
        this.relocateStage();

        this.photoRect = this.zone.photo.rect;
        this.photoSrc = this.zone.photo.image;
        this.photoImg = new Image();
        this.photoImg.crossOrigin = 'anonymous';
        this.photoImg.onload = () => this.draw();
        this.photoImg.src = this.zone.photo.image;
      }

      // Follow the theme's variant picker. Dawn updates the product info in place rather
      // than re-rendering it, so this panel and the customer's design both survive.
      this.way = this.colorwayFor(this.currentVariant());
      this.applyVariant(this.currentVariant());
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (ev) => {
          if (ev?.data?.variant) this.applyVariant(ev.data.variant);
        });
      }

      // Real stock colours for THIS method: thread for embroidery, vinyl sheets for vinyl.
      if (this.dataset.colors) {
        fetch(this.dataset.colors)
          .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
          .then((d) => {
            const list = d[this.zone.method];
            if (!list?.length) return;
            this.palette = list;
            // An element created before the palette arrived holds a fallback colour that
            // may not be stocked. Move it to the nearest real one rather than leaving an
            // unbuyable swatch selected.
            const has = (hex) => list.some((c) => c.hex.toLowerCase() === hex.toLowerCase());
            const stock = (hex) => (hex && !has(hex) ? this.nearestColor(hex).hex : hex);
            for (const side of this.sides) {
              for (const el of this.design[side] || []) {
                el.color = stock(el.color);
                // Repair every BLOCK, not just block 0. Sport seeds three threads from
                // the colours it was digitised in, and two of them would otherwise stay
                // on the fallback palette and be unbuyable.
                if (el.colors) el.colors = el.colors.map(stock);
              }
            }
            this.render();
          })
          .catch((err) => console.error('[personalizer] colours failed', err));
      }

      // Two kinds of face live in this catalogue. The stitched alphabets are per-glyph
      // images of real embroidery, so they are embroidery-only — vinyl is cut, not sewn.
      // The webfonts are the shop's own uploads, which Zepto offered on everything.
      if (this.dataset.fonts) {
        fetch(this.dataset.fonts)
          .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
          .then((d) => {
            if (this.zone.method === 'embroidery') {
              this.stitched = (d.fonts || []).filter((f) => f.method === this.zone.method);
            }
            return this.loadWebfonts(d.webfonts || []);
          })
          .then(() => {
            // The panel builds its first element before this fetch resolves, so that
            // element defaulted to Arial. Mod Chic is the house face — 13 of the last 18
            // personalized orders — so re-point anything the customer has not chosen for
            // themselves. `fontAuto` is what separates "never touched" from "picked Arial".
            const d = this.defaultFont();
            this.els.forEach((e) => {
              if (!e.fontAuto) return;
              e.font = d;
              // Same reduction the pick handler does. On embroidery `d` is a stitched
              // alphabet, and a value it has no glyph for would otherwise vanish from the
              // preview while staying in the text field.
              if (d.glyphs && e.type === 'text') {
                e.value = PersonalizerPanel.stitchable(e.value, d);
                // The element was built against whatever font existed before this fetch,
                // so it holds one colour and a free-form size. Give it the new face's
                // colourway and one of its real sizes, exactly as picking it by hand does.
                this.seedBlockColors(e);
              }
            });
            // The monogram zones seed HERE — the pair font they open on only exists now.
            this.seedDefault();
            this.editor();
            this.render();
          })
          .catch((err) => {
            console.error('[personalizer] fonts failed', err);
            // No catalogue means no monogram, but the panel must still open on something.
            this.seedDefault();
            this.render();
          });
      }

      if (!this.noPreview) fetch(this.dataset.icons)
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
        .then((cat) => {
          // VINYL CUTS A SILHOUETTE FROM ONE SHEET OF ONE COLOUR. The library came from
          // embroidery files and 73 of its 77 designs are multi-colour, so offering them
          // on a planner lets a customer order something that cannot be made — the
          // failure surfaces at the machine, after they have paid.
          // Filtered on the data, not a hand-kept list: add single-colour art and it
          // appears here on its own.
          // VINYL IS CUT FROM ONE SHEET OF ONE COLOUR. The stitch library is embroidery
          // art — 73 of its 77 designs are multi-colour and simply cannot be produced in
          // vinyl. So vinyl gets SYMBOLS instead: silhouettes that take the customer's
          // vinyl colour and scale without resolution loss.
          this.icons = this.zone.method === 'vinyl'
            ? (cat.symbols || []).map((sym) => ({ ...sym, symbol: true, name: sym.name }))
            // Filtered on GEOMETRY, same rule as the fonts: a design that cannot be sewn
            // here at safe density is not on offer here. See iconFits().
            : (cat.icons || []).filter((ic) => this.iconFits(ic));
          // An empty library after filtering must not leave a button that adds nothing.
          if (!this.icons.length) {
            const btn = this.querySelector('[data-pz="add"][data-type="icon"]');
            if (btn) {
              btn.disabled = true;
              btn.title = 'No icons available for this material yet';
            }
          }
          const e = this.els.find((o) => o.id === this.sel);
          if (e?.type === 'icon' && !e.img && !e.value && this.icons[0]) this.pickIcon(e, this.icons[0]);
          this.editor();
        })
        .catch((err) => {
          console.error('[personalizer] icon catalogue failed', err);
          const btn = this.querySelector('[data-pz="add"][data-type="icon"]');
          if (btn) { btn.disabled = true; btn.title = 'Icons are unavailable right now'; }
        });

      // Text seeds immediately, as it always has. A monogram zone waits for the fonts —
      // seeding a text box here would win the els.length race and the monogram would never
      // appear.
      if (!this.dataset.fonts || (this.zone.default_element || 'text') !== 'monogram') {
        this.seedDefault();
      }
      this.render();
    }
  }

  /** True if a meaningful share of the image is see-through. Sampled on a small offscreen
   *  copy — reading 10MP of pixel data to answer a yes/no is wasteful. */
  function hasAlpha(img, sample = 96, minFraction = 0.02) {
    const c = document.createElement('canvas');
    c.width = c.height = sample;
    const x = c.getContext('2d', { willReadFrequently: true });
    x.drawImage(img, 0, 0, sample, sample);
    const px = x.getImageData(0, 0, sample, sample).data;
    let clear = 0;
    for (let i = 3; i < px.length; i += 4) if (px[i] < 250) clear++;
    return clear / (px.length / 4) >= minFraction;
  }

  /** '#rrggbb' -> linear-space rgb. glTF baseColorFactor is LINEAR, not sRGB; feeding it
   *  sRGB values makes every tint come out noticeably too light. */
  function hexToRgb(hex) {
    const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ''));
    if (!m) return null;
    const n = parseInt(m[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }

  function hexToLinear(hex) {
    const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ''));
    if (!m) return null;
    const n = parseInt(m[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
      const c = v / 255;
      return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
  }

  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  if (!customElements.get('personalizer-panel')) {
    customElements.define('personalizer-panel', PersonalizerPanel);
  }
})();
